import { api } from "../api";
import { openModal, closeModal, modalBody, onModalClose } from "../components/modal";
import { confirmDialog } from "../components/confirm-dialog";
import { initDropdownCheck } from "../components/dropdown-check";
import { canWrite, writeOnly, readOnlyNotice } from "../utils/permissions";
import { showToast } from "../utils/toast";
import { Icons } from "../utils/icons";
import { exportTimetable, exportLecturerTimetable, exportOverview } from "../utils/export-service";
import {
  WEEKDAY_SHORT, isoDate, parseIso, mondayOf, addDays, isoWeek,
  formatDate, formatDayMonth,
} from "../utils/date-utils";
import type {
  Lesson, TimeSlot, SchoolClass, Subject, Lecturer, LessonConflict, SchoolSettings, SeriesPreview,
  DayContext, SchoolClosure, Room, ClassAttendance, StudentAbsence, AbsenceKind, LessonStatus,
} from "../types";
import { esc } from "../utils/html";

/**
 * Stundenplan einer Klasse als Wochenansicht.
 *
 * Zeilen sind die Blöcke des Stundenrasters, Spalten die Wochentage. Ein Termin steht in
 * der Zeile des ersten Blocks, den er berührt; Termine außerhalb des Rasters landen in
 * einer eigenen Zeile darunter, damit sie nicht verschwinden.
 *
 * Wann ein einzelner Dozent Unterricht hat, steht bei ihm selbst (Einstellungen,
 * Stundenplan, Dozenten), so wie die Praktikumseinsätze beim Schüler stehen.
 */

let weekStart = mondayOf(new Date());
let selectedClassId: number | null = null;

/**
 * Die Wochenansicht zeigt entweder den Plan einer Klasse oder die Belegung eines Raums.
 * Die Raumansicht ist bewusst nur zum Nachsehen da („wer ist diese Woche in A101?"):
 * Unterricht anzulegen braucht eine Klasse, und die wählt man dann dort.
 */
type ViewMode = "klasse" | "alle" | "raum" | "anwesenheit";
let viewMode: ViewMode = "klasse";

/**
 * Ansicht „Alle Klassen": die zu planenden Klassen nebeneinander, so wie Schulen ihren
 * Wochenplan oft als Tabelle führen. Die Auswahl bleibt je Browser gemerkt.
 */
const OVERVIEW_KEY = "stundenplan.uebersicht.klassen";
let overviewClassIds: number[] = [];
/** Ferien und Praktikum je Klasse und Tag; in der Übersicht gilt beides je Spalte. */
let overviewContext = new Map<number, DayContext[]>();
let selectedRoomId: number | null = null;
let attendance: ClassAttendance | null = null;

let classes: SchoolClass[] = [];
let subjects: Subject[] = [];
let lecturers: Lecturer[] = [];
let rooms: Room[] = [];
let slots: TimeSlot[] = [];
let lessons: Lesson[] = [];
let dayContext: DayContext[] = [];
let settings: SchoolSettings | null = null;
let currentEl: HTMLElement | null = null;

const weekEnd = () => addDays(weekStart, 6);

function vorplanungEnabled(): boolean {
  return settings?.stundenplanVorplanungEnabled === true;
}

/** Beschriftung eines Raums in der Auswahl: Kürzel oder Name, dazu die Platzzahl. */
function roomLabel(r: Room): string {
  const name = r.shortName || r.name;
  const extras: string[] = [];
  if (r.capacity !== null) extras.push(`${r.capacity} Plätze`);
  if (!r.active) extras.push("inaktiv");
  return extras.length === 0 ? name : `${name} (${extras.join(", ")})`;
}

/** Stammdaten einmal laden; sie ändern sich während der Planung nicht. */
async function loadBase() {
  [classes, subjects, lecturers, rooms, slots, settings] = await Promise.all([
    api.classes.list(),
    api.stundenplan.subjects.list(),
    api.stundenplan.lecturers.list(),
    api.stundenplan.rooms.list(),
    api.stundenplan.raster.list(),
    api.settings.get(),
  ]);
  if (selectedClassId === null) selectedClassId = classes[0]?.id ?? null;
  if (overviewClassIds.length === 0) overviewClassIds = loadOverviewSelection();
}

/** Gemerkte Auswahl der Übersicht; ohne Auswahl alle aktiven Klassen. */
function loadOverviewSelection(): number[] {
  const active = classes.filter(c => c.active !== false).map(c => +c.id);
  try {
    const stored = JSON.parse(localStorage.getItem(OVERVIEW_KEY) || "[]");
    if (Array.isArray(stored)) {
      const known = stored.map(Number).filter(id => active.includes(id));
      if (known.length > 0) return known;
    }
  } catch { /* gesperrter Speicher: dann eben alle */ }
  return active;
}

function storeOverviewSelection() {
  try { localStorage.setItem(OVERVIEW_KEY, JSON.stringify(overviewClassIds)); } catch { /* egal */ }
}

/** Klassen der Übersicht in der Reihenfolge der Klassenliste. */
function overviewClasses(): SchoolClass[] {
  return classes.filter(c => overviewClassIds.includes(+c.id));
}

async function loadWeek() {
  const von = isoDate(weekStart);
  const bis = isoDate(weekEnd());

  // Anwesenheit: Schueler und Termine kommen gemeinsam, samt der bereits gerechneten
  // Fehlstellen. Die Ueberlappungslogik bleibt im Backend, wo sie getestet ist.
  if (viewMode === "anwesenheit") {
    lessons = [];
    dayContext = [];
    attendance = selectedClassId
      ? await api.stundenplan.attendance.forClass(selectedClassId, von, bis)
      : null;
    return;
  }
  attendance = null;

  // Alle Klassen: einmal den Plan der Schule, dazu Ferien und Praktikum je Klasse.
  if (viewMode === "alle") {
    dayContext = [];
    const ids = overviewClasses().map(c => +c.id);
    const [plan, contexts] = await Promise.all([
      ids.length ? api.stundenplan.plan(von, bis) : Promise.resolve([] as Lesson[]),
      Promise.all(ids.map(id => api.stundenplan.weekContext(von, bis, id).then(r => [id, r.days] as const))),
    ]);
    const wanted = new Set(ids);
    lessons = plan.filter(l => wanted.has(+l.classId));
    overviewContext = new Map(contexts);
    return;
  }

  // Raumansicht: nur die Termine. Ferien haengen an einer Klasse und waeren hier nicht zu
  // beantworten, weil im Raum mehrere Klassen sitzen.
  if (viewMode === "raum") {
    dayContext = [];
    lessons = selectedRoomId
      ? await api.stundenplan.plan(von, bis, { raum: selectedRoomId })
      : [];
    return;
  }

  if (!selectedClassId) { lessons = []; dayContext = []; return; }
  // Den Stand je Fach zeigen die Kacheln („✓ fertig“, „über Soll“), die Serienvorschau und
  // die Auswertung; eine eigene Leiste über dem Raster gibt es nicht mehr.
  const [plan, context] = await Promise.all([
    api.stundenplan.plan(von, bis, { klasse: selectedClassId }),
    api.stundenplan.weekContext(von, bis, selectedClassId),
  ]);
  lessons = plan;
  dayContext = context.days;
}

/** Was an einem Tag gegen Unterricht spricht; leer, wenn nichts dagegen spricht. */
function dayNote(iso: string): string {
  const day = dayContext.find(d => d.date === iso);
  if (!day) return "";
  const parts = [...day.closures];
  if (day.practice) parts.push("Praktikum");
  return parts.join(" · ");
}

// ── Raster ──

/** Der erste Block, den ein Termin berührt; null, wenn er außerhalb des Rasters liegt. */
function slotOf(lesson: Lesson): TimeSlot | null {
  return slots.find(s => lesson.startTime < s.endTime && s.startTime < lesson.endTime) ?? null;
}

/** Indizes des ersten und letzten Blocks, den ein Termin berührt; -1 wenn keiner. */
function slotSpan(lesson: Lesson): { first: number; last: number } {
  const touched = slots
    .map((s, i) => (lesson.startTime < s.endTime && s.startTime < lesson.endTime ? i : -1))
    .filter(i => i >= 0);
  return touched.length === 0
    ? { first: -1, last: -1 }
    : { first: touched[0], last: touched[touched.length - 1] };
}

/**
 * Die Metazeile einer Kachel. Bei einer Vertretung steht der geplante Dozent
 * durchgestrichen vor dem tatsächlichen, damit im Plan sichtbar bleibt, wer eigentlich
 * vorgesehen war. Genau das ist der Grund, den Ausfall als Zustand zu führen statt zu
 * löschen.
 */
function lessonMeta(lesson: Lesson): string {
  if (viewMode === "raum") {
    return [lesson.className, lesson.lecturerName].filter(Boolean).map(esc).join(" · ");
  }
  const parts: string[] = [];
  if (lesson.substituteLecturerName) {
    parts.push(lesson.lecturerName
      ? `<span class="lb-replaced">${esc(lesson.lecturerName)}</span> ${esc(lesson.substituteLecturerName)}`
      : esc(lesson.substituteLecturerName));
  } else if (lesson.lecturerName) {
    parts.push(esc(lesson.lecturerName));
  }
  if (lesson.substituteRoom) {
    parts.push(lesson.room
      ? `<span class="lb-replaced">${esc(lesson.room)}</span> ${esc(lesson.substituteRoom)}`
      : esc(lesson.substituteRoom));
  } else if (lesson.room) {
    parts.push(esc(lesson.room));
  }
  return parts.join(" · ");
}

/**
 * Zeilenhöhe einer Rasterzeile in Pixeln. Muss zu `.timetable-wrapper tbody td` im CSS
 * passen: Die Höhe einer Kachel wird daraus gerechnet, weil sie absolut positioniert ist
 * und die Zellenhöhe nicht mehr selbst mitbringt.
 */
const ROW_HEIGHT = 72;

/**
 * Spaltenaufteilung für parallelen Unterricht eines Tages, nach demselben Muster wie
 * computeColumnLayout in planning.ts.
 *
 * Zwei Termine sind parallel, sobald sich ihre Blockbereiche berühren. Jeder bekommt eine
 * freie Spalte, und `total` sagt, in wie viele Spalten die Zelle geteilt werden muss.
 * Ohne das verdeckte ein Termin über zwei Blöcke schlicht den, der im zweiten Block schon
 * stand.
 */
function computeLessonLayout(dayLessons: Lesson[]): Map<number, { col: number; total: number }> {
  const layout = new Map<number, { col: number; total: number }>();
  const spans = new Map<number, { first: number; last: number }>();
  for (const l of dayLessons) spans.set(l.id, slotSpan(l));

  const sorted = [...dayLessons].sort((a, b) => {
    const sa = spans.get(a.id)!, sb = spans.get(b.id)!;
    return sa.first !== sb.first ? sa.first - sb.first : a.id - b.id;
  });

  for (const l of sorted) {
    const s = spans.get(l.id)!;
    const overlapping = sorted.filter(o => {
      if (o.id === l.id) return false;
      const so = spans.get(o.id)!;
      return s.first <= so.last && so.first <= s.last;
    });

    // Die erste Spalte, die kein Nachbar schon belegt.
    const used = new Set(
      overlapping.map(o => layout.get(o.id)?.col).filter((c): c is number => c !== undefined)
    );
    let col = 0;
    while (used.has(col)) col++;

    // Geteilt wird nach dem dichtesten Block, den dieser Termin berührt. Sonst wären zwei
    // Termine unterschiedlich breit, obwohl sie nebeneinander liegen.
    let maxOverlap = 1;
    for (let i = s.first; i <= s.last; i++) {
      const n = dayLessons.filter(o => {
        const so = spans.get(o.id)!;
        return so.first <= i && so.last >= i;
      }).length;
      maxOverlap = Math.max(maxOverlap, n);
    }
    layout.set(l.id, { col, total: maxOverlap });
  }
  return layout;
}

/** UE mit deutschem Komma. */
function fmtUe(n: number): string {
  return (Math.round(n * 100) / 100).toLocaleString("de-DE");
}

/**
 * Kennzeichen eines Termins hinter dem Fach: Status, Fortschritt im Lehrplan, Vorplanung.
 * Alles steht hinter dem Kürzel und nie davor, damit das Fach immer zuerst lesbar ist;
 * vorher lag das Auge für „nicht sichtbar" über dem Anfang des Kürzels.
 */
function lessonBadges(lesson: Lesson): string {
  const notVisible = vorplanungEnabled() && !lesson.visibleToStudent;
  const preliminary = vorplanungEnabled() && lesson.isPreliminary;
  const parts: string[] = [];
  if (lesson.status === "entfall") {
    parts.push(`<span class="ab-cancelled" title="${esc(lesson.changeNote || "Fällt aus")}">Entfällt</span>`);
  } else if (lesson.status === "vertretung") {
    parts.push(`<span class="ab-substitute" title="${esc(lesson.changeNote || "Vertretung")}">Vertretung</span>`);
  }
  if (lesson.fachFertig) {
    parts.push(`<span class="ab-done" title="Mit diesem Termin sind die ${fmtUe(lesson.ueZiel ?? 0)} UE laut Lehrplan erreicht">✓ fertig</span>`);
  } else if (lesson.ueberSoll) {
    parts.push(`<span class="ab-over" title="Das Fach hatte seine ${fmtUe(lesson.ueZiel ?? 0)} UE laut Lehrplan schon vor diesem Termin">über Soll</span>`);
  }
  if (preliminary) parts.push(`<span class="ab-preliminary" title="Vorläufig">Vorläufig</span>`);
  if (notVisible) parts.push(`<span class="ab-visibility" title="Nicht sichtbar für Schüler">${Icons.eyeSlash}</span>`);
  return parts.join("");
}

/** Fortschritt als Tooltip: „Berufskunde: 14 von 20 UE". */
function progressTitle(lesson: Lesson): string {
  if (lesson.ueKumuliert == null || lesson.ueZiel == null) return lesson.subjectName;
  return `${lesson.subjectName}: ${fmtUe(lesson.ueKumuliert)} von ${fmtUe(lesson.ueZiel)} UE`;
}

function lessonBlock(lesson: Lesson, style = ""): string {
  const notVisible = vorplanungEnabled() && !lesson.visibleToStudent;
  const cancelled = lesson.status === "entfall";
  const meta = lessonMeta(lesson);
  return `
    <div class="lesson-block${cancelled ? " is-cancelled" : ""}" data-lesson="${lesson.id}"
         title="${esc(progressTitle(lesson))}"
         style="background:${esc(lesson.subjectColor)};${notVisible ? "opacity:.6;" : ""}${style}">
      <div class="lb-name"><span class="lb-subject">${esc(lesson.subjectShortName || lesson.subjectName)}</span>${lessonBadges(lesson)}</div>
      ${meta ? `<div class="lb-meta">${meta}</div>` : ""}
      <div class="lb-meta">${esc(lesson.startTime)} bis ${esc(lesson.endTime)}</div>
    </div>`;
}

function renderGrid(): string {
  if (slots.length === 0) {
    return `<div class="alert alert-info">
      Es ist noch kein Stundenraster hinterlegt. Lege es unter Einstellungen, Stundenplan,
      Stundenraster an, damit die Wochenansicht Zeilen bekommt.
    </div>`;
  }

  const days = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  // Termine am Wochenende sind selten, sollen aber nicht verschwinden.
  const weekendUsed = lessons.some(l => {
    const wd = parseIso(l.lessonDate).getDay();
    return wd === 0 || wd === 6;
  });
  if (weekendUsed) days.push(addDays(weekStart, 5), addDays(weekStart, 6));

  const today = isoDate(new Date());
  const placed = new Set<number>();

  // Termine je Tag, samt Spaltenaufteilung fuer parallelen Unterricht. Einmal vorab,
  // damit die Aufteilung ueber alle Zeilen desselben Tages dieselbe bleibt.
  const perDay = new Map<string, { lessons: Lesson[]; layout: Map<number, { col: number; total: number }> }>();
  for (const day of days) {
    const iso = isoDate(day);
    const dayLessons = lessons.filter(l => l.lessonDate === iso && slotSpan(l).first >= 0);
    dayLessons.forEach(l => placed.add(l.id));
    perDay.set(iso, { lessons: dayLessons, layout: computeLessonLayout(dayLessons) });
  }

  const cell = (iso: string, slotId: number | null, index: number, loose: Lesson[] = []) => {
    const day = perDay.get(iso);
    // In der Zeile "Außerhalb" gibt es keine Blockzuordnung; dort werden die Termine
    // schlicht untereinander gestapelt.
    const starting = slotId === null ? loose
      : (day?.lessons ?? []).filter(l => slotSpan(l).first === index);
    // Belegt heisst: irgendein Termin liegt ueber dieser Zeile, auch wenn er weiter oben
    // beginnt. Nur eine wirklich freie Zelle laedt zum Anlegen ein.
    const occupied = slotId === null ? loose.length > 0
      : (day?.lessons ?? []).some(l => {
          const s = slotSpan(l);
          return s.first <= index && s.last >= index;
        });

    const classes = [
      occupied ? "" : "cell-empty",
      iso === today ? "tp-today" : "",
      dayNote(iso) ? "tp-closed" : "",
    ].filter(Boolean).join(" ");
    const slotAttr = slotId !== null ? ` data-slot="${slotId}"` : "";

    // Ohne Blockbezug bleibt es beim Stapel: Diese Termine haben keine Zeile, ueber die
    // sich eine Hoehe rechnen liesse.
    if (slotId === null) {
      const stack = starting.length === 0 ? ""
        : `<div class="lesson-stack">${starting.map(l => lessonBlock(l)).join("")}</div>`;
      return `<td class="${classes}" data-day="${iso}">${stack}</td>`;
    }

    const content = starting.map(l => {
      const s = slotSpan(l);
      // Hoehe aus der Zahl der beruehrten Bloecke, wie die Dauer eines Einsatzes in der
      // Praktikumsplanung. ROW_HEIGHT muss zur Zeilenhoehe im CSS passen.
      const height = (s.last - s.first + 1) * ROW_HEIGHT - 8;
      const lay = day?.layout.get(l.id) ?? { col: 0, total: 1 };
      // Parallelen Unterricht nebeneinander legen, statt ihn zu verdecken. Vorher lag ein
      // Termin ueber zwei Bloecken per rowspan auf der Zelle darunter, und was dort schon
      // stand, war nicht mehr zu sehen.
      const pos = lay.total > 1
        ? `left:calc(${(lay.col / lay.total) * 100}% + 2px);width:calc(${(1 / lay.total) * 100}% - 4px);right:auto;`
        : "";
      return lessonBlock(l, `height:${height}px;${pos}`);
    }).join("");

    return `<td class="${classes}" data-day="${iso}"${slotAttr}>${content}</td>`;
  };

  const rows = slots.map((slot, index) => {
    const cells = days.map(day => cell(isoDate(day), slot.id, index)).join("");
    return `<tr>
      <td class="tp-slot">
        <div class="kw-label">${esc(slot.label)}</div>
        <div class="kw-dates">${esc(slot.startTime)} bis ${esc(slot.endTime)}</div>
      </td>${cells}
    </tr>`;
  }).join("");

  // Alles, was in keinen Block passt, bekommt eine eigene Zeile.
  const loose = lessons.filter(l => !placed.has(l.id));
  const looseRow = loose.length === 0 ? "" : `
    <tr>
      <td class="tp-slot">
        <div class="kw-label">Außerhalb</div>
        <div class="kw-dates">kein Block</div>
      </td>
      ${days.map(day => {
        const iso = isoDate(day);
        return cell(iso, null, -1, loose.filter(l => l.lessonDate === iso));
      }).join("")}
    </tr>`;

  return `
    <div class="planning-wrapper timetable-wrapper">
      <table>
        <thead><tr>
          <th></th>
          ${days.map(d => {
            const iso = isoDate(d);
            const note = dayNote(iso);
            return `<th class="${[iso === today ? "tp-today" : "", note ? "tp-closed" : ""].filter(Boolean).join(" ")}">
              <div class="tp-day">${WEEKDAY_SHORT[(d.getDay() + 6) % 7]}</div>
              <div class="tp-date">${formatDayMonth(d)}</div>
              ${note ? `<div class="tp-note" title="${esc(note)}">${esc(note)}</div>` : ""}
            </th>`;
          }).join("")}
        </tr></thead>
        <tbody>${rows}${looseRow}</tbody>
      </table>
    </div>`;
}

// ── Alle Klassen ──

/** Was an diesem Tag bei dieser Klasse gegen Unterricht spricht. */
function classDayNote(classId: number, iso: string): string {
  const day = overviewContext.get(classId)?.find(d => d.date === iso);
  if (!day) return "";
  const parts = [...day.closures];
  if (day.practice) parts.push("Praktikum");
  return parts.join(" · ");
}

/** Bemerkung eines Termins in der Übersicht: Notiz und Grund einer Abweichung. */
function lessonRemark(l: Lesson): string {
  return [l.note, l.changeNote].filter(Boolean).join(" · ");
}

/**
 * Wochenplan aller gewählten Klassen nebeneinander.
 *
 * Zeilen sind Tag und Block, je Klasse vier Spalten: Fach, Dozent, Bemerkung, Raum. Ein
 * Unterricht über mehrere Blöcke steht in jeder Blockzeile, so wie in den Tabellen, mit
 * denen Schulen bisher planen. Paralleler Unterricht derselben Klasse wird in der Zelle
 * untereinander gestapelt.
 */
function renderOverview(): string {
  if (slots.length === 0) {
    return `<div class="alert alert-info">
      Es ist noch kein Stundenraster hinterlegt. Lege es unter Einstellungen, Stundenplan,
      Stundenraster an, damit die Wochenansicht Zeilen bekommt.
    </div>`;
  }
  const cls = overviewClasses();
  if (cls.length === 0) return `<div class="alert alert-info">Bitte oben mindestens eine Klasse wählen.</div>`;

  const days = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  const weekendUsed = lessons.some(l => { const wd = parseIso(l.lessonDate).getDay(); return wd === 0 || wd === 6; });
  if (weekendUsed) days.push(addDays(weekStart, 5), addDays(weekStart, 6));
  const today = isoDate(new Date());

  // Termine je Klasse und Tag mit ihrem Blockbereich, einmal vorab.
  const byClassDay = new Map<string, { lesson: Lesson; first: number; last: number }[]>();
  for (const l of lessons) {
    const key = `${l.classId}|${l.lessonDate}`;
    if (!byClassDay.has(key)) byClassDay.set(key, []);
    byClassDay.get(key)!.push({ lesson: l, ...slotSpan(l) });
  }

  const lessonLine = (l: Lesson) => `
    <div class="ov-lesson${l.status === "entfall" ? " is-cancelled" : ""}" data-lesson="${l.id}" title="${esc(progressTitle(l))}"
         style="--subject:${esc(l.subjectColor)}">
      <span class="ov-subject">${esc(l.subjectShortName || l.subjectName)}</span>${lessonBadges(l)}
    </div>`;
  const lecturerOf = (l: Lesson) => l.substituteLecturerName
    ? (l.lecturerName ? `<span class="lb-replaced">${esc(l.lecturerName)}</span> ` : "") + esc(l.substituteLecturerName)
    : esc(l.lecturerName || "");
  const roomOf = (l: Lesson) => l.substituteRoom
    ? (l.room ? `<span class="lb-replaced">${esc(l.room)}</span> ` : "") + esc(l.substituteRoom)
    : esc(l.room || "");
  const stack = (items: string[]) => items.map(x => `<div class="ov-line">${x || "&nbsp;"}</div>`).join("");

  const rows: string[] = [];
  for (const day of days) {
    const iso = isoDate(day);
    slots.forEach((slot, index) => {
      const cells = cls.map(c => {
        const here = (byClassDay.get(`${c.id}|${iso}`) ?? [])
          .filter(x => x.first <= index && x.last >= index)
          .map(x => x.lesson);
        const note = classDayNote(+c.id, iso);
        if (here.length === 0) {
          const attrs = `data-day="${iso}" data-slot="${slot.id}" data-class="${c.id}"`;
          return note
            ? `<td colspan="4" class="ov-closed ov-group-start" ${attrs}>${index === 0 ? esc(note) : ""}</td>`
            : `<td colspan="4" class="ov-empty ov-group-start" ${attrs}></td>`;
        }
        return `
          <td class="ov-fach ov-group-start">${here.map(lessonLine).join("")}</td>
          <td class="ov-small">${stack(here.map(lecturerOf))}</td>
          <td class="ov-small ov-remark">${stack(here.map(l => esc(lessonRemark(l))))}</td>
          <td class="ov-small">${stack(here.map(roomOf))}</td>`;
      }).join("");

      // Der Tag steht in jeder Zeile als eigene Zelle, beschriftet nur in der ersten.
      // Mit rowspan wäre die erste Zelle einer Zeile mal der Tag, mal der Block, und die
      // beiden festgehaltenen Spalten verrutschten beim Querscrollen.
      const dayCell = `<td class="ov-day${iso === today ? " tp-today" : ""}">${index === 0 ? `
             <div class="tp-day">${WEEKDAY_SHORT[(day.getDay() + 6) % 7]}</div>
             <div class="tp-date">${formatDate(day)}</div>` : ""}
           </td>`;
      rows.push(`<tr class="${index === 0 ? "ov-day-start" : ""}">
        ${dayCell}
        <td class="ov-slot"><div class="kw-label">${esc(slot.label)}</div><div class="kw-dates">${esc(slot.startTime)} bis ${esc(slot.endTime)}</div></td>
        ${cells}
      </tr>`);
    });
  }

  // Termine ausserhalb des Rasters fallen hier nicht still weg: ein Hinweis nennt sie.
  const loose = lessons.filter(l => slotSpan(l).first < 0);
  const looseNote = loose.length === 0 ? "" : `
    <div class="alert alert-info" style="margin-top:.75rem;font-size:.8125rem">
      ${loose.length} ${loose.length === 1 ? "Termin liegt" : "Termine liegen"} außerhalb des Stundenrasters und
      ${loose.length === 1 ? "steht" : "stehen"} nur in der Ansicht „Nach Klasse".
    </div>`;

  return `
    <div class="planning-wrapper overview-wrapper">
      <table class="ov-table">
        <thead>
          <tr>
            <th rowspan="2" class="ov-day-head">Tag</th>
            <th rowspan="2" class="ov-slot-head">Block</th>
            ${cls.map(c => `<th colspan="4" class="ov-class ov-group-start">${esc(c.name)}</th>`).join("")}
          </tr>
          <tr>
            ${cls.map(() => `<th class="ov-group-start">Fach</th><th>Dozent</th><th>Bemerkung</th><th>Raum</th>`).join("")}
          </tr>
        </thead>
        <tbody>${rows.join("")}</tbody>
      </table>
    </div>
    ${looseNote}`;
}

// ── Formular ──

/**
 * Rückfrage bei einem Termin, der zu einer Serie gehört: gilt die Aktion nur für diesen
 * Termin oder für die ganze Serie?
 *
 * Ein eigener Dialog statt confirmDialog, weil dort nur eine Zusage möglich ist. Er
 * ersetzt das Unterrichtsfenster, weil modal.ts nur einen Slot hat; die Aufrufer öffnen
 * es bei Abbruch deshalb wieder.
 */
function askSeriesScope(opts: {
  title: string; message: string; singleLabel: string; seriesLabel: string; danger?: boolean;
}): Promise<"single" | "series" | null> {
  return new Promise(resolve => {
    let decided = false;
    const decide = (value: "single" | "series" | null) => { if (!decided) { decided = true; resolve(value); } };

    openModal(opts.title, `
      <p style="font-size:.875rem;color:var(--gray-700)">${esc(opts.message)}</p>
      <div class="form-actions">
        <button class="btn btn-secondary" id="sc-cancel">Abbrechen</button>
        <button class="btn ${opts.danger ? "btn-danger" : "btn-secondary"}" id="sc-single">${esc(opts.singleLabel)}</button>
        <button class="btn ${opts.danger ? "btn-danger-fill" : "btn-primary"}" id="sc-series">${esc(opts.seriesLabel)}</button>
      </div>`);

    // Deckt Escape, Klick auf den Hintergrund und das X ab: alles gilt als Abbruch.
    onModalClose(() => decide(null));

    const mb = modalBody();
    mb.querySelector("#sc-cancel")!.addEventListener("click", () => { decide(null); closeModal(); });
    mb.querySelector("#sc-single")!.addEventListener("click", () => { decide("single"); closeModal(); });
    mb.querySelector("#sc-series")!.addEventListener("click", () => { decide("series"); closeModal(); });
  });
}

/**
 * Fächer, die für eine Klasse in Frage kommen: die im Lehrplan ihres Ausbildungsmodells,
 * mit der Stufe am Eintrag. Bewusst nicht auf die laufende Stufe eingeschränkt, weil
 * Vorausplanen ein normaler Vorgang ist.
 *
 * Hat die Klasse kein Modell oder ist dessen Lehrplan leer, gibt es keine Grundlage zum
 * Filtern; dann stehen alle Fächer zur Wahl und das Formular sagt, warum.
 */
function relevantSubjects(classId: number | null): { subject: Subject; stageLabel: string | null }[] {
  const model = classes.find(c => c.id === classId)?.courseModelId ?? null;
  const active = subjects.filter(s => s.active);
  type Entry = { subject: Subject; stageLabel: string | null; stage: number | null };
  const inPlan = model === null ? [] : active
    .map((subject): Entry | null => {
      const entry = subject.curricula.find(c => c.modelId === model);
      return entry ? { subject, stageLabel: entry.stageLabel ?? "alle Stufen", stage: entry.stage } : null;
    })
    .filter((e): e is Entry => e !== null)
    // Nach Stufe sortieren, damit der Ausbildungsverlauf ablesbar bleibt.
    .sort((a, b) => (a.stage ?? 0) - (b.stage ?? 0) || a.subject.name.localeCompare(b.subject.name));
  return inPlan.length > 0 ? inPlan : active.map(subject => ({ subject, stageLabel: null }));
}

function openLessonForm(opts: { lesson?: Lesson; day?: string; slot?: TimeSlot; classId?: number }) {
  const { lesson, day, slot } = opts;
  // In der Ansicht „Alle Klassen" kommt die Klasse aus der angeklickten Spalte.
  const presetClassId = opts.classId ?? selectedClassId;
  const isEdit = !!lesson;
  const readOnly = !canWrite();
  const disabled = readOnly ? " disabled" : "";

  const classOpts = classes.map(c =>
    `<option value="${c.id}" ${(lesson?.classId ?? presetClassId) === c.id ? "selected" : ""}>${esc(c.name)}</option>`).join("");
  /**
   * Auswahl der Fächer für die gerade gewählte Klasse. Das Fach eines bestehenden
   * Termins bleibt immer enthalten, auch wenn es inzwischen nicht mehr zum Modell passt:
   * sonst würde die Auswahl es beim Öffnen still austauschen.
   */
  const subjectOptionsFor = (classId: number | null, selectedId: number | null): string => {
    const entries = relevantSubjects(classId);
    const options = entries.map(e =>
      `<option value="${e.subject.id}" ${selectedId === e.subject.id ? "selected" : ""}>` +
      `${esc(e.subject.name)}${e.stageLabel ? ` · ${esc(e.stageLabel)}` : ""}</option>`).join("");

    const current = selectedId ? subjects.find(s => s.id === selectedId) : undefined;
    const missing = current && !entries.some(e => e.subject.id === current.id)
      ? `<option value="${current.id}" selected>${esc(current.name)} · nicht im Lehrplan der Klasse</option>`
      : "";
    return `<option value="">Fach wählen</option>${options}${missing}`;
  };

  const initialClassId = lesson?.classId ?? presetClassId;
  const subjectOpts = subjectOptionsFor(initialClassId, lesson?.subjectId ?? null);
  const activeLecturers = lecturers.filter(l => l.active);
  const lecturerOpts = `<option value="">Kein Dozent</option>` + activeLecturers
    .map(l => `<option value="${l.id}" ${lesson?.lecturerId === l.id ? "selected" : ""}>${esc(`${l.firstName} ${l.lastName}`.trim())}</option>`).join("");
  // Inaktive Raeume bleiben in der Liste, wenn der Termin sie gerade benutzt. Sonst
  // verschwaende ein Raum, den jemand deaktiviert hat, still aus einem bestehenden Termin.
  const activeRooms = rooms.filter(r => r.active || r.id === lesson?.roomId);
  const roomOpts = `<option value="">Kein Raum</option>` + activeRooms
    .map(r => `<option value="${r.id}" ${lesson?.roomId === r.id ? "selected" : ""}>${esc(roomLabel(r))}</option>`).join("");
  // Der Unterricht wird ueber Bloecke erfasst; die Uhrzeiten leitet das Backend daraus
  // ab. Ueber zwei Auswahlen, damit auch Doppelstunden ohne Tipperei gehen.
  const currentFrom = lesson ? (lesson.slotFromId ?? slotOf(lesson)?.id) : slot?.id;
  const currentTo = lesson ? (lesson.slotToId ?? currentFrom) : slot?.id;
  const slotOptions = (selected: number | null | undefined) => slots
    .map(s => `<option value="${s.id}" ${selected === s.id ? "selected" : ""}>${esc(s.label)} · ${esc(s.startTime)} bis ${esc(s.endTime)}</option>`).join("");

  const lessonDate = lesson?.lessonDate || day || isoDate(weekStart);

  /**
   * Entfall und Vertretung, nur an einem vorhandenen Termin.
   *
   * Im Normalfall ist das eine einzelne Auswahl. Die Zusatzfelder erscheinen erst, wenn
   * sie gebraucht werden: Wer nichts abweichen lässt, soll auch nichts davon sehen.
   * Gespeichert wird mit „Speichern" wie alles andere; dass ein Statuswechsel technisch
   * an einem Datum hängt und eine Planänderung an der Serie hängen kann, regelt der
   * Speichern-Ablauf und nicht der Nutzer.
   */
  const statusBlock = !isEdit ? "" : `
    <div class="form-group">
      <label>Status</label>
      <select id="l-status"${disabled}>
        <option value="regulaer" ${lesson!.status === "regulaer" ? "selected" : ""}>Findet wie geplant statt</option>
        <option value="entfall" ${lesson!.status === "entfall" ? "selected" : ""}>Fällt aus</option>
        <option value="vertretung" ${lesson!.status === "vertretung" ? "selected" : ""}>Findet statt, mit Vertretung</option>
      </select>
    </div>
    <div id="l-sub-fields" class="form-grid ${lesson!.status === "vertretung" ? "" : "hidden"}">
      <div class="form-group">
        <label>Vertretung durch</label>
        <select id="l-sub-lecturer"${disabled}>
          <option value="">Derselbe Dozent</option>
          ${activeLecturers.map(l => `<option value="${l.id}" ${lesson!.substituteLecturerId === l.id ? "selected" : ""}>${esc(`${l.firstName} ${l.lastName}`.trim())}</option>`).join("")}
        </select>
      </div>
      <div class="form-group">
        <label>Anderer Raum</label>
        <select id="l-sub-room"${disabled}>
          <option value="">Derselbe Raum</option>
          ${activeRooms.map(r => `<option value="${r.id}" ${lesson!.substituteRoomId === r.id ? "selected" : ""}>${esc(roomLabel(r))}</option>`).join("")}
        </select>
      </div>
    </div>
    <div class="form-group ${lesson!.status === "regulaer" ? "hidden" : ""}" id="l-change-note-row">
      <label>Bemerkung zur Abweichung</label>
      <input id="l-change-note" value="${esc(lesson!.changeNote || "")}"${disabled} placeholder="z.B. verlegt auf den 19.09." />
      <small class="text-muted">Ein Ausfall bleibt im Plan stehen, gekennzeichnet, und zählt nicht mehr im Soll-Abgleich.</small>
    </div>`;

  // Nur beim Anlegen: Serie oder Einzeltermin. Ein vorhandener Termin wird immer
  // einzeln bearbeitet und loest sich damit aus seiner Serie.
  // Wiederholung ist der Normalfall, deshalb steht sie direkt beim Zeitpunkt statt
  // als Anhaengsel am Ende.
  const seriesBlock = isEdit ? "" : `
    <div class="form-group" style="margin-top:.75rem">
      <label class="checkbox-label"><input type="checkbox" id="l-series" checked${disabled} /> Wöchentlich wiederholen</label>
    </div>
    <div id="l-series-fields" class="form-grid">
      <div class="form-group">
        <label>Rhythmus</label>
        <select id="l-interval"${disabled}>
          <option value="1">Jede Woche</option>
          <option value="2">Alle 2 Wochen</option>
          <option value="3">Alle 3 Wochen</option>
          <option value="4">Alle 4 Wochen</option>
        </select>
      </div>
      <div class="form-group"><label>Letzter Termin</label><input type="date" id="l-to" value="${esc(lessonDate)}"${disabled} /></div>
      <div class="form-group col-span-2">
        <div class="alert alert-info hidden" id="l-range-hint" style="font-size:.8125rem;margin:0"></div>
      </div>
      <div class="form-group col-span-2">
        <div class="alert hidden" id="l-preview" style="font-size:.8125rem;margin:0"></div>
      </div>
    </div>`;

  const vorplanungBlock = !vorplanungEnabled() ? "" : `
    <fieldset>
      <legend>Sichtbarkeit</legend>
      <div class="form-grid">
        <div class="form-group">
          <label class="checkbox-label"><input type="checkbox" id="l-visible" ${lesson ? (lesson.visibleToStudent ? "checked" : "") : (settings?.stundenplanDefaultVisible ? "checked" : "")}${disabled} /> Für Schüler sichtbar</label>
        </div>
        <div class="form-group">
          <label class="checkbox-label"><input type="checkbox" id="l-preliminary" ${lesson ? (lesson.isPreliminary ? "checked" : "") : (settings?.stundenplanDefaultPreliminary ? "checked" : "")}${disabled} /> Vorläufig</label>
        </div>
      </div>
    </fieldset>`;

  const seriesNote = isEdit && lesson!.seriesId && !lesson!.detached
    ? `<div class="alert alert-info" style="font-size:.8125rem">Dieser Termin gehört zu einer Serie. Nach dem Speichern gilt er als einzeln bearbeitet und wird von Serienänderungen nicht mehr überschrieben.</div>`
    : "";

  openModal(isEdit ? "Unterricht bearbeiten" : "Neuer Unterricht", `
    <fieldset>
      <legend>Unterricht</legend>
      <div class="form-grid">
        <div class="form-group">
          <label>Fach *</label>
          <select id="l-subject"${disabled}>${subjectOpts}</select>
          <small class="text-muted" id="l-subject-hint">${subjectHint(initialClassId)}</small>
        </div>
        <div class="form-group"><label>Klasse *</label><select id="l-class"${disabled}>${classOpts}</select></div>
      </div>
    </fieldset>

    <fieldset>
      <legend>Zeitpunkt</legend>
      <div class="form-group"><label>${isEdit ? "Datum *" : "Erster Termin *"}</label><input type="date" id="l-date" value="${esc(lessonDate)}"${disabled} /></div>
      <div class="form-grid">
        <div class="form-group"><label>Von Block *</label><select id="l-slot-from"${disabled}>${slotOptions(currentFrom)}</select></div>
        <div class="form-group"><label>Bis Block *</label><select id="l-slot-to"${disabled}>${slotOptions(currentTo)}</select></div>
      </div>
      ${seriesBlock}
    </fieldset>

    <fieldset>
      <legend>${isEdit ? "Dozent, Raum und Status" : "Dozent und Raum"}</legend>
      <div class="form-grid">
        <div class="form-group"><label>Dozent</label><select id="l-lecturer"${disabled}>${lecturerOpts}</select></div>
        <div class="form-group">
          <label>Raum</label>
          <select id="l-room"${disabled}>${roomOpts}</select>
          <small class="text-muted hidden" id="l-room-hint"></small>
        </div>
      </div>
      ${statusBlock}
    </fieldset>

    ${vorplanungBlock}

    <div class="form-group">
      <label>Notiz</label>
      <input id="l-note" value="${esc(lesson?.note || "")}"${disabled} placeholder="Interner Vermerk, für Schüler nicht sichtbar" />
    </div>
    ${seriesNote}
    <div class="alert alert-error hidden" id="l-error"></div>
    <div class="form-actions">
      ${isEdit ? writeOnly(`<button class="btn btn-danger" id="l-delete" style="margin-right:auto">Löschen</button>`) : ""}
      <button class="btn btn-secondary" id="l-cancel">${readOnly ? "Schließen" : "Abbrechen"}</button>
      ${writeOnly(`<button class="btn btn-primary" id="l-save">Speichern</button>`)}
    </div>`, { wide: true, stickyActions: true });

  const mb = modalBody();
  const $ = <T extends HTMLElement>(sel: string) => mb.querySelector<T>(sel);

  const slotFrom = $<HTMLSelectElement>("#l-slot-from")!;
  const slotTo = $<HTMLSelectElement>("#l-slot-to")!;
  const dateInput = $<HTMLInputElement>("#l-date")!;
  const classSelect = $<HTMLSelectElement>("#l-class")!;
  const subjectSelect = $<HTMLSelectElement>("#l-subject")!;

  mb.querySelector("#l-cancel")!.addEventListener("click", closeModal);
  if (readOnly) return;

  // Wechselt die Klasse, wechselt auch das Ausbildungsmodell und damit die Fächer.
  classSelect.addEventListener("change", () => {
    const classId = +classSelect.value || null;
    subjectSelect.innerHTML = subjectOptionsFor(classId, +subjectSelect.value || null);
    $<HTMLElement>("#l-subject-hint")!.textContent = subjectHint(classId);
  });

  /** Zeiten der gewählten Blöcke; dieselbe Ableitung wie im Backend. */
  const times = () => {
    const from = slots.find(s => s.id === +slotFrom.value);
    const to = slots.find(s => s.id === +slotTo.value) || from;
    if (!from || !to) return null;
    return from.position <= to.position
      ? { start: from.startTime, end: to.endTime }
      : { start: to.startTime, end: from.endTime };
  };

  // Die beiden Auswahlen halten sich gegenseitig in der richtigen Reihenfolge: sonst
  // laeuft ein frueherer Bis-Block erst beim Speichern in "Der letzte Block liegt vor
  // dem ersten".
  const alignSlots = (moved: "from" | "to") => {
    const from = slots.find(s => s.id === +slotFrom.value);
    const to = slots.find(s => s.id === +slotTo.value);
    if (!from || !to || to.position >= from.position) return;
    if (moved === "from") slotTo.value = slotFrom.value;
    else slotFrom.value = slotTo.value;
  };
  slotFrom.addEventListener("change", () => alignSlots("from"));
  slotTo.addEventListener("change", () => alignSlots("to"));

  const showError = (message: string) => {
    const box = $<HTMLElement>("#l-error")!;
    box.textContent = message;
    box.classList.remove("hidden");
  };

  const lecturerSelect = $<HTMLSelectElement>("#l-lecturer")!;

  /**
   * Beschriftet die Dozentenauswahl mit der Verfuegbarkeit zu den geplanten Terminen.
   * Wer keine Anwesenheiten hinterlegt hat, bleibt unbeschriftet: das waere eine
   * Aussage, die die Daten nicht hergeben.
   */
  const markAvailability = async () => {
    const seriesBox = $<HTMLInputElement>("#l-series");
    const isSeries = !isEdit && !!seriesBox?.checked;
    const von = dateInput.value;
    const bis = isSeries ? $<HTMLInputElement>("#l-to")!.value : dateInput.value;
    const span = times();
    if (!von || !bis || !span) return;

    try {
      const result = await api.stundenplan.lecturerAvailability({
        von, bis, startTime: span.start, endTime: span.end,
        klasse: +classSelect.value || undefined,
        weekday: ((parseIso(von).getDay() + 6) % 7) + 1,
        intervalWeeks: isSeries ? +$<HTMLSelectElement>("#l-interval")!.value : 1,
      });
      const byId = new Map(result.lecturers.map(l => [l.id, l]));
      for (const option of Array.from(lecturerSelect.options)) {
        const id = +option.value;
        const lecturer = activeLecturers.find(l => l.id === id);
        if (!lecturer) continue;
        const name = `${lecturer.firstName} ${lecturer.lastName}`.trim();
        const info = byId.get(id);
        if (!info || !info.known) { option.textContent = name; continue; }
        option.textContent = info.missing === 0
          ? `${name} (verfügbar)`
          : result.dates <= 1
            ? `${name} (nicht da)`
            : `${name} (an ${info.missing} von ${result.dates} Terminen nicht da)`;
      }
    } catch { /* Markierung ist Komfort, kein Muss */ }
  };
  markAvailability();

  const roomSelect = $<HTMLSelectElement>("#l-room")!;
  const roomHint = $<HTMLElement>("#l-room-hint")!;

  /**
   * Beschriftet die Raumauswahl mit der Belegung im gewaehlten Zeitfenster und warnt,
   * wenn der Raum kleiner ist als die Klasse. Beides bleibt ein Hinweis: Teilungsunterricht
   * und bewusst enge Raeume sind gaengige Praxis, genau wie bei den Kollisionen.
   *
   * Geprueft wird nur der erste Termin. Bei einer Serie waere jede Woche einzeln zu
   * pruefen; das ist viel Last fuer eine Auskunft, die der Konflikthinweis nach dem
   * Speichern ohnehin vollstaendig nachliefert.
   */
  const markRooms = async () => {
    const span = times();
    const datum = dateInput.value;
    if (!datum || !span) return;

    const klasse = classes.find(c => c.id === +classSelect.value);
    const setHint = () => {
      const room = rooms.find(r => r.id === +roomSelect.value);
      if (!room || room.capacity === null || !klasse || klasse.studentCount <= room.capacity) {
        roomHint.classList.add("hidden");
        return;
      }
      roomHint.textContent = `${room.shortName || room.name} hat ${room.capacity} Plätze, die Klasse hat ${klasse.studentCount} Schüler.`;
      roomHint.classList.remove("hidden");
    };

    try {
      const available = await api.stundenplan.rooms.available({
        datum, von: span.start, bis: span.end, ausser: lesson?.id,
      });
      const byId = new Map(available.map(r => [r.id, r]));
      for (const option of Array.from(roomSelect.options)) {
        const room = rooms.find(r => r.id === +option.value);
        if (!room) continue;
        const info = byId.get(room.id);
        // Nicht in der Antwort heisst inaktiv; dann steht der Raum nur noch hier, weil
        // der bearbeitete Termin ihn benutzt.
        option.textContent = !info ? roomLabel(room)
          : info.free ? roomLabel(room)
          : `${roomLabel(room)} · belegt: ${info.occupiedBy || "anderer Unterricht"}`;
      }
    } catch { /* Markierung ist Komfort, kein Muss */ }
    setHint();
  };
  markRooms();
  roomSelect.addEventListener("change", markRooms);

  const statusSelect = $<HTMLSelectElement>("#l-status");
  // Vertretungsfelder und Bemerkung folgen dem Status: im Normalfall bleibt nur die
  // Auswahl stehen.
  if (statusSelect) {
    const subFields = $<HTMLElement>("#l-sub-fields")!;
    const noteRow = $<HTMLElement>("#l-change-note-row")!;
    statusSelect.addEventListener("change", () => {
      subFields.classList.toggle("hidden", statusSelect.value !== "vertretung");
      noteRow.classList.toggle("hidden", statusSelect.value === "regulaer");
    });
  }

  const markBoth = () => { markAvailability(); markRooms(); };
  dateInput.addEventListener("change", markBoth);
  slotFrom.addEventListener("change", markBoth);
  slotTo.addEventListener("change", markBoth);
  classSelect.addEventListener("change", markRooms);

  if (!isEdit) {
    const seriesBox = $<HTMLInputElement>("#l-series")!;
    const seriesFields = $<HTMLElement>("#l-series-fields")!;
    const toInput = $<HTMLInputElement>("#l-to")!;
    const rangeHint = $<HTMLElement>("#l-range-hint")!;

    const previewBox = $<HTMLElement>("#l-preview")!;
    const intervalSelect = $<HTMLSelectElement>("#l-interval")!;

    seriesBox.addEventListener("change", () => seriesFields.classList.toggle("hidden", !seriesBox.checked));

    /**
     * Stammt „Letzter Termin" aus dem Vorschlag, darf die Vorschau ihn auf das Datum
     * kürzen, an dem das Fach laut Lehrplan fertig ist. Hat jemand selbst ein Datum
     * eingetragen, bleibt es bei einem Hinweis mit Knopf.
     */
    let endFromSuggestion = false;
    toInput.addEventListener("input", () => { endFromSuggestion = false; });

    // Der Zeitraum kommt aus der Stufe, in der das Fach laut Zuordnung liegt.
    // Das erspart das Abtippen und hält Plan und Ausbildungsverlauf zusammen.
    const suggestRange = async () => {
      const classId = +classSelect.value;
      const subjectId = +subjectSelect.value;
      if (!classId || !subjectId) return;
      try {
        const result = await api.stundenplan.seriesRange(classId, subjectId);
        if (result.range) {
          // Der erste Termin bleibt, was der Nutzer angeklickt hat; vorgeschlagen wird
          // nur das Ende der Stufe.
          toInput.value = result.range.end;
          endFromSuggestion = true;
          rangeHint.textContent = `Vorschlag aus dem Ausbildungsmodell: bis ${formatDate(result.range.end)}`
            + (result.hoursPerWeek != null ? `, ${fmtUe(result.hoursPerWeek)} UE pro Woche vorgesehen.` : ".");
        } else {
          rangeHint.textContent = result.reason || "Kein Vorschlag möglich, bitte Zeitraum selbst wählen.";
        }
        rangeHint.classList.remove("hidden");
      } catch { /* Vorschlag ist Komfort, kein Muss */ }
    };

    /**
     * Wann ist das Fach mit dieser Serie fertig? Gefragt wird das Backend, weil nur dort
     * Ferien, Praktikumswochen und die schon vorhandenen Termine bekannt sind.
     */
    let previewRun = 0;
    const showPreview = async () => {
      const run = ++previewRun;
      const classId = +classSelect.value;
      const subjectId = +subjectSelect.value;
      if (!seriesBox.checked || !classId || !subjectId || !slotFrom.value || !dateInput.value || !toInput.value
          || toInput.value < dateInput.value) {
        previewBox.classList.add("hidden");
        return;
      }
      let p: SeriesPreview;
      try {
        p = await api.stundenplan.series.preview({
          classId, subjectId,
          slotFromId: +slotFrom.value, slotToId: +slotTo.value || null,
          weekday: ((parseIso(dateInput.value).getDay() + 6) % 7) + 1,
          intervalWeeks: +intervalSelect.value,
          startDate: dateInput.value, endDate: toInput.value,
        });
      } catch { previewBox.classList.add("hidden"); return; }
      if (run !== previewRun) return; // eine neuere Anfrage ist unterwegs

      const name = subjects.find(x => x.id === subjectId)?.name ?? "Das Fach";
      const kind = (cls: string) => { previewBox.className = `alert ${cls}`; };
      // Ziel aus der Fächergruppe: was dort nach den anderen Fächern noch frei ist.
      const g = p.gruppe;
      const basis = g
        ? `${name}: aus der Gruppe „${g.name}“ sind noch ${fmtUe(p.ziel ?? 0)} von ${fmtUe(g.pflichtUe)} UE frei `
          + `(${fmtUe(g.andere)} UE bei anderen Fächern eingeplant), ${fmtUe(p.bereitsVerplant)} bereits verplant. `
        : `${name}: ${fmtUe(p.ziel ?? 0)} UE im Lehrplan, ${fmtUe(p.bereitsVerplant)} bereits verplant. `;

      // Vorgeschlagenes Ende still auf den Tag kürzen, an dem das Fach fertig ist.
      if (p.ueberzaehlig > 0 && p.endeVorschlag && endFromSuggestion && p.endeVorschlag >= dateInput.value) {
        toInput.value = p.endeVorschlag;
        endFromSuggestion = false;
        rangeHint.textContent = g
          ? `Letzter Termin auf ${formatDate(p.endeVorschlag)} gesetzt: dann sind die ${fmtUe(p.ziel!)} UE erreicht, die in der Gruppe „${g.name}“ noch frei waren.`
          : `Letzter Termin auf ${formatDate(p.endeVorschlag)} gesetzt: dann sind die ${fmtUe(p.ziel!)} UE laut Lehrplan erreicht.`;
        rangeHint.classList.remove("hidden");
        showPreview();
        markAvailability();
        return;
      }

      const serie = `${p.termine} ${p.termine === 1 ? "Termin" : "Termine"} mit je ${fmtUe(p.uePerTermin)} UE`;
      if (p.ziel === null) {
        kind("alert-info");
        previewBox.textContent = `${serie}. Für ${name} ist im Lehrplan keine UE-Zahl hinterlegt, deshalb gibt es keinen Hinweis, wann das Fach fertig ist.`;
      } else if (p.ziel === 0) {
        kind("alert-warning");
        const alle = p.termine === 1 ? "Der Termin dieser Serie wäre" : `Alle ${p.termine} Termine dieser Serie wären`;
        previewBox.textContent = g
          ? `Die Gruppe „${g.name}“ ist mit ${fmtUe(g.pflichtUe)} UE schon voll eingeplant. ${alle} zu viel.`
          : `Für ${name} sind im Lehrplan 0 UE vorgesehen. ${alle} zu viel.`;
      } else if (p.fertigAm === null) {
        const rest = p.ziel - p.bereitsVerplant - p.neu;
        kind("alert-info");
        previewBox.textContent = `${basis}Mit dieser Serie (${serie}) fehlen danach noch ${fmtUe(rest)} UE.`;
      } else if (p.ueberzaehlig === 0) {
        kind("alert-success");
        previewBox.textContent = `${basis}Mit dieser Serie ist das Fach am ${formatDate(p.fertigAm)} vollständig.`;
      } else {
        kind("alert-warning");
        const zuViel = `${p.ueberzaehlig} ${p.ueberzaehlig === 1 ? "Termin danach wäre" : "Termine danach wären"} zu viel`;
        previewBox.innerHTML = `${esc(basis)}`
          + `Das Fach ist am ${esc(formatDate(p.fertigAm))} vollständig, ${esc(zuViel)}.`
          + (p.endeVorschlag
            ? ` <button type="button" class="btn btn-sm btn-secondary" id="l-preview-apply" style="margin-left:.25rem">Serie am ${esc(formatDate(p.endeVorschlag))} enden lassen</button>`
            : " Die Serie bringt das Fach nicht mehr voran.");
        previewBox.querySelector("#l-preview-apply")?.addEventListener("click", () => {
          toInput.value = p.endeVorschlag!;
          showPreview();
          markAvailability();
        });
      }
      previewBox.classList.remove("hidden");
    };
    let previewTimer: number | undefined;
    const schedulePreview = () => {
      window.clearTimeout(previewTimer);
      previewTimer = window.setTimeout(showPreview, 250);
    };

    const suggestAndMark = async () => { await suggestRange(); schedulePreview(); await markAvailability(); };
    subjectSelect.addEventListener("change", suggestAndMark);
    classSelect.addEventListener("change", suggestAndMark);
    seriesBox.addEventListener("change", () => { markAvailability(); schedulePreview(); });
    toInput.addEventListener("change", () => { markAvailability(); schedulePreview(); });
    intervalSelect.addEventListener("change", () => { markAvailability(); schedulePreview(); });
    for (const el of [dateInput, slotFrom, slotTo]) el.addEventListener("change", schedulePreview);
    // Ist das Fach schon gewählt (Formular über eine Zelle geöffnet), gleich vorschlagen.
    if (subjectSelect.value) suggestAndMark();
  }

  /** askSeriesScope belegt denselben Modal-Platz wie dieses Formular. Nach einem
   *  Abbruch muss es deshalb neu aufgebaut werden, statt den Nutzer im Nichts
   *  stehen zu lassen. */
  const reopen = () => openLessonForm({ lesson });

  $<HTMLButtonElement>("#l-delete")?.addEventListener("click", async () => {
    if (!lesson!.seriesId) {
      const ok = await confirmDialog({
        title: "Termin löschen",
        message: `${lesson!.subjectName} am ${formatDate(lesson!.lessonDate)} wird gelöscht.`,
        confirmLabel: "Löschen", danger: true,
      });
      // Die Rückfrage legt sich über dieses Formular, statt es zu ersetzen. Bei
      // Abbruch bleibt es also einfach stehen, beim Löschen muss es weg.
      if (!ok) return;
      closeModal();
      try {
        await api.stundenplan.lessons.delete(lesson!.id);
        await refresh();
        showToast("Termin gelöscht");
      } catch (err: any) { showToast("Fehler: " + err.message); }
      return;
    }

    const scope = await askSeriesScope({
      title: "Termin löschen",
      message: "Dieser Termin gehört zu einer Serie. Einzeln bearbeitete Termine der Serie bleiben in jedem Fall bestehen.",
      singleLabel: "Nur diese Unterrichtseinheit",
      seriesLabel: "Ganze Serie",
      danger: true,
    });
    if (!scope) { reopen(); return; }

    try {
      if (scope === "single") {
        await api.stundenplan.lessons.delete(lesson!.id);
        showToast("Termin gelöscht");
      } else {
        const { deleted } = await api.stundenplan.series.delete(lesson!.seriesId!);
        showToast(`Serie gelöscht, ${deleted} Termine entfernt`);
      }
      await refresh();
    } catch (err: any) { showToast("Fehler: " + err.message); }
  });

  mb.querySelector("#l-save")!.addEventListener("click", async () => {
    const base = {
      classId: +classSelect.value || null,
      subjectId: +subjectSelect.value || null,
      lecturerId: +lecturerSelect.value || null,
      roomId: +roomSelect.value || null,
      slotFromId: +slotFrom.value || null,
      slotToId: +slotTo.value || null,
      note: $<HTMLInputElement>("#l-note")!.value.trim() || null,
      visibleToStudent: vorplanungEnabled() ? $<HTMLInputElement>("#l-visible")!.checked : undefined,
      isPreliminary: vorplanungEnabled() ? $<HTMLInputElement>("#l-preliminary")!.checked : undefined,
    };
    // Gehoert zu base und wird genauso vor dem Scope-Dialog gegriffen, nur gehen die
    // beiden Serien-Endpunkte unterschiedlich damit um (Datum bzw. Wochentag).
    const lessonDate = dateInput.value;
    if (!base.classId) { showError("Klasse ist erforderlich"); return; }
    if (!base.subjectId) { showError("Fach ist erforderlich"); return; }
    if (!base.slotFromId) { showError("Bitte einen Block aus dem Stundenraster wählen"); return; }

    // Der Status geht nicht ueber base, weil er einen eigenen Endpunkt hat und nur fuer
    // diesen einen Tag gilt. null heisst "unveraendert": Wer nur den Raum tauscht, soll
    // den Termin nicht nebenbei als einzeln bearbeitet markieren.
    let statusChange: {
      status: LessonStatus;
      substituteLecturerId: number | null;
      substituteRoomId: number | null;
      changeNote: string | null;
    } | null = null;
    if (statusSelect) {
      const status = statusSelect.value as LessonStatus;
      const next = {
        status,
        substituteLecturerId: status === "vertretung" ? (+$<HTMLSelectElement>("#l-sub-lecturer")!.value || null) : null,
        substituteRoomId: status === "vertretung" ? (+$<HTMLSelectElement>("#l-sub-room")!.value || null) : null,
        changeNote: status === "regulaer" ? null : ($<HTMLInputElement>("#l-change-note")!.value.trim() || null),
      };
      if (status === "vertretung" && !next.substituteLecturerId && !next.substituteRoomId) {
        showError("Eine Vertretung braucht einen anderen Dozenten oder einen anderen Raum");
        return;
      }
      const unchanged = next.status === lesson!.status
        && next.substituteLecturerId === lesson!.substituteLecturerId
        && next.substituteRoomId === lesson!.substituteRoomId
        && next.changeNote === lesson!.changeNote;
      if (!unchanged) statusChange = next;
    }

    // Aenderung an einem Serientermin: gilt sie nur hier oder fuer die ganze Serie?
    //
    // Achtung: askSeriesScope ist selbst ein openModal und ersetzt damit den Inhalt des
    // einen Modal-Bodys. Nach dem Dialog gibt es das Formular nicht mehr, weder die
    // Schaltflaeche noch die Fehlerbox. Alle Formularwerte stehen deshalb schon oben in
    // "base"; danach darf nichts mehr aus dem Formular gelesen oder gesetzt werden.
    let scope: "single" | "series" = "single";
    let formGone = false;
    if (isEdit && lesson!.seriesId) {
      const choice = await askSeriesScope({
        title: "Änderung speichern",
        message: "Dieser Termin gehört zu einer Serie. Einzeln bearbeitete Termine der Serie bleiben in jedem Fall unverändert.",
        singleLabel: "Nur diese Unterrichtseinheit",
        seriesLabel: "Ganze Serie",
      });
      if (!choice) { reopen(); return; }
      scope = choice;
      formGone = true;
    }

    const saveBtn = mb.querySelector<HTMLButtonElement>("#l-save");
    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = "Speichern…"; }
    try {
      let conflicts: LessonConflict[] = [];
      if (isEdit && scope === "series") {
        // Der Status gilt immer nur fuer diesen einen Tag, nie fuer die Serie. Er wird
        // ZUERST gesetzt: Damit gilt der Termin als einzeln bearbeitet, und die
        // anschliessende Serienerzeugung laesst ihn stehen, statt ihn zu loeschen und
        // ohne Status neu anzulegen.
        if (statusChange) await api.stundenplan.lessons.setStatus(lesson!.id, statusChange);

        // Die Serie behaelt Rhythmus und Zeitraum; geaendert werden die Werte aus dem
        // Formular. Ein anderes Datum verschiebt dabei den Wochentag der ganzen Serie.
        const series = await api.stundenplan.series.get(lesson!.seriesId!);
        const result = await api.stundenplan.series.update(series.id, {
          ...series,
          ...base,
          weekday: ((parseIso(lessonDate).getDay() + 6) % 7) + 1,
        });
        // Der bearbeitete Termin ist einzeln bearbeitet, entweder schon vorher oder durch
        // den Statuswechsel gerade eben, und wird von der Serienaenderung ausgelassen.
        // Ohne diesen Nachzug bliebe er als einziger auf dem alten Stand, ausgerechnet
        // der, den man gerade offen hatte.
        if (lesson!.detached || statusChange) {
          await api.stundenplan.lessons.update(lesson!.id, { ...base, lessonDate });
        }
        showToast(`Serie gespeichert, ${result.created} Termine aktualisiert`);
      } else if (isEdit) {
        const result = await api.stundenplan.lessons.update(lesson!.id, { ...base, lessonDate });
        conflicts = result.conflicts;
        // Nach den Plandaten, damit die Kollisionspruefung des Statuswechsels bereits den
        // fertigen Stand sieht und ihre Hinweise die des ersten Aufrufs ersetzen koennen.
        if (statusChange) {
          const withStatus = await api.stundenplan.lessons.setStatus(lesson!.id, statusChange);
          conflicts = withStatus.conflicts;
        }
      } else if ($<HTMLInputElement>("#l-series")!.checked) {
        const result = await api.stundenplan.series.create({
          ...base,
          weekday: ((parseIso(lessonDate).getDay() + 6) % 7) + 1,
          intervalWeeks: +$<HTMLSelectElement>("#l-interval")!.value,
          startDate: lessonDate,
          endDate: $<HTMLInputElement>("#l-to")!.value,
        });
        conflicts = result.conflicts;
        showToast(`${result.created} Termine angelegt`);
      } else {
        const result = await api.stundenplan.lessons.create({ ...base, lessonDate });
        conflicts = result.conflicts;
        showToast("Termin angelegt");
      }
      closeModal();
      await refresh();
      // Hinweise, kein Verbot: Teilungsunterricht ist eine gewollte Doppelbelegung.
      if (conflicts.length > 0) showConflicts(conflicts);
      else if (isEdit && scope === "single") showToast("Termin gespeichert");
    } catch (err: any) {
      // Ohne Formular gibt es keine Fehlerbox mehr: Meldung als Toast und zurueck ins
      // Formular, damit die Eingaben nicht kommentarlos verloren gehen.
      if (formGone) { showToast("Fehler: " + err.message); reopen(); return; }
      showError(err.message);
      if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = "Speichern"; }
    }
  });
}

/** Hinweis unter der Fachauswahl: woher die angebotenen Fächer kommen. */
function subjectHint(classId: number | null): string {
  const klasse = classes.find(c => c.id === classId);
  if (!klasse) return "";
  if (!klasse.courseModelId) return "Die Klasse hat kein Ausbildungsmodell, deshalb stehen alle Fächer zur Wahl.";
  const hasPlan = subjects.some(s => s.curricula.some(c => c.modelId === klasse.courseModelId));
  return hasPlan
    ? "Fächer aus dem Lehrplan des Ausbildungsmodells dieser Klasse."
    : "Der Lehrplan des Ausbildungsmodells ist noch leer, deshalb stehen alle Fächer zur Wahl.";
}

/** Doppelbelegungen als Hinweis nach dem Speichern, gebündelt nach Meldung. */
function showConflicts(conflicts: LessonConflict[]) {
  const byMessage = new Map<string, string[]>();
  for (const c of conflicts) {
    if (!byMessage.has(c.message)) byMessage.set(c.message, []);
    byMessage.get(c.message)!.push(formatDate(c.lessonDate));
  }
  const lines = [...byMessage.entries()]
    .map(([message, dates]) => `<li>${esc(message)}: ${dates.slice(0, 5).map(esc).join(", ")}${dates.length > 5 ? ` und ${dates.length - 5} weitere` : ""}</li>`)
    .join("");
  openModal("Gespeichert, mit Hinweisen", `
    <p style="font-size:.875rem;color:var(--gray-700)">
      Der Unterricht wurde angelegt. Diese Überschneidungen sind uns aufgefallen; sie
      können gewollt sein, etwa bei Teilungsunterricht.
    </p>
    <ul style="font-size:.875rem;color:var(--gray-700);padding-left:1.25rem">${lines}</ul>
    <div class="form-actions"><button class="btn btn-primary" id="ck-ok">Verstanden</button></div>`);
  modalBody().querySelector("#ck-ok")!.addEventListener("click", closeModal);
}


// ── Ferien ──
//
// Unterrichtsfreie Zeiten liegen hier statt in den Einstellungen, weil sie beim Planen
// gebraucht werden: dieselbe Stelle wie die Ferien in der Praktikumsplanung.

function openClosureForm(closure?: SchoolClosure, onDone?: () => void) {
  const isEdit = !!closure;
  const today = isoDate(new Date());

  // Im Standard sind alle Klassen angehakt. Damit deckt dieselbe Auswahl beide Faelle
  // ab: einzelne Klassen ausnehmen, oder Ferien nur fuer bestimmte Klassen eintragen.
  const selected = closure && closure.classIds.length > 0
    ? new Set(closure.classIds)
    : new Set(classes.map(c => c.id));

  const classItems = classes.length === 0
    ? `<div class="dropdown-check-item text-muted">Keine Klassen vorhanden</div>`
    : classes.map(c => `<label class="dropdown-check-item"><input type="checkbox" value="${c.id}" ${selected.has(c.id) ? "checked" : ""} /> ${esc(c.name)}</label>`).join("");

  openModal(isEdit ? "Ferien bearbeiten" : "Ferien eintragen", `
    <div class="form-group">
      <label>Bezeichnung *</label>
      <input id="c-name" value="${esc(closure?.name || "")}" placeholder="z.B. Herbstferien" />
    </div>
    <div class="form-grid">
      <div class="form-group"><label>Von *</label><input type="date" id="c-start" value="${esc(closure?.startDate || today)}" /></div>
      <div class="form-group"><label>Bis *</label><input type="date" id="c-end" value="${esc(closure?.endDate || closure?.startDate || today)}" /></div>
    </div>
    <div class="form-group">
      <label>Gilt für</label>
      <div class="dropdown-check" id="c-classes">
        <button type="button" class="dropdown-check-btn">Alle Klassen</button>
        <div class="dropdown-check-panel dropdown-check-panel-left">${classItems}</div>
      </div>
      <small class="text-muted">Klassen abwählen, die weiter Unterricht haben.</small>
    </div>
    <div class="alert alert-info" style="font-size:.8125rem">
      In diesem Zeitraum fällt Unterricht aus. Bereits erzeugte Termine bleiben stehen;
      neu angelegte Serien lassen die Tage aus.
    </div>
    <div class="alert alert-error hidden" id="c-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="c-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="c-save">Speichern</button>
    </div>`);

  const mb = modalBody();
  mb.querySelector("#c-cancel")!.addEventListener("click", closeModal);

  // Alles angehakt entspricht "alle Klassen", deshalb zeigt der Knopf dann den
  // Standardtext statt einer Aufzaehlung.
  const classDropdown = initDropdownCheck(
    mb.querySelector("#c-classes") as HTMLElement,
    () => { /* Auswahl wird erst beim Speichern gelesen */ },
    { allSelectedShowsDefault: true },
  );

  mb.querySelector("#c-save")!.addEventListener("click", async () => {
    const picked = classDropdown.getValues().map(Number);
    const data = {
      name: (mb.querySelector("#c-name") as HTMLInputElement).value.trim(),
      startDate: (mb.querySelector("#c-start") as HTMLInputElement).value,
      endDate: (mb.querySelector("#c-end") as HTMLInputElement).value,
      // Alle ausgewaehlt wird als leere Liste gespeichert: der Eintrag gilt dann auch
      // fuer Klassen, die es heute noch nicht gibt.
      classIds: picked.length === classes.length ? [] : picked,
    };
    const box = mb.querySelector("#c-error") as HTMLElement;
    const fail = (msg: string) => { box.textContent = msg; box.classList.remove("hidden"); };
    if (!data.name) return fail("Bezeichnung ist erforderlich");
    if (!data.startDate || !data.endDate) return fail("Zeitraum ist erforderlich");
    if (data.endDate < data.startDate) return fail("Das Ende liegt vor dem Beginn");
    if (classes.length > 0 && picked.length === 0) return fail("Bitte mindestens eine Klasse wählen");

    const saveBtn = mb.querySelector("#c-save") as HTMLButtonElement;
    saveBtn.disabled = true; saveBtn.textContent = "Speichern…";
    try {
      if (isEdit) await api.stundenplan.closures.update(closure!.id, data);
      else await api.stundenplan.closures.create(data);
      closeModal();
      await refresh();
      showToast("Ferien gespeichert");
      onDone?.();
    } catch (err: any) {
      fail(err.message);
      saveBtn.disabled = false; saveBtn.textContent = "Speichern";
    }
  });

  (mb.querySelector("#c-name") as HTMLInputElement).focus();
}

async function openClosureList() {
  let closures: SchoolClosure[] = [];
  try {
    closures = await api.stundenplan.closures.list();
  } catch (err: any) { showToast("Fehler: " + err.message); return; }

  const rows = closures.length === 0
    ? `<tr><td colspan="4" class="empty-state">Noch keine Ferien eingetragen</td></tr>`
    : closures.map(c => `
      <tr>
        <td style="font-weight:500">${esc(c.name)}</td>
        <td class="text-muted">${c.startDate === c.endDate
          ? esc(formatDate(c.startDate))
          : `${esc(formatDate(c.startDate))} bis ${esc(formatDate(c.endDate))}`}</td>
        <td class="text-muted">${c.classIds.length === 0
          ? "Alle Klassen"
          : esc(classes.filter(k => c.classIds.includes(k.id)).map(k => k.name).join(", "))}</td>
        <td style="white-space:nowrap;text-align:right">
          <button class="btn btn-secondary btn-sm" data-edit="${c.id}">Bearbeiten</button>
          <button class="btn btn-danger btn-sm" data-del="${c.id}">Löschen</button>
        </td>
      </tr>`).join("");

  openModal("Ferien verwalten", `
    <div class="table-wrap">
      <table>
        <thead><tr><th>Bezeichnung</th><th style="width:220px">Zeitraum</th><th>Gilt für</th><th style="width:190px"></th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="cl-close">Schließen</button>
      <button class="btn btn-primary" id="cl-add">+ Ferien eintragen</button>
    </div>`, { wide: true });

  const mb = modalBody();
  mb.querySelector("#cl-close")!.addEventListener("click", closeModal);
  mb.querySelector("#cl-add")!.addEventListener("click", () => openClosureForm(undefined, openClosureList));

  mb.querySelector("tbody")!.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;

    const edit = target.closest("[data-edit]") as HTMLElement | null;
    if (edit) {
      const closure = closures.find(c => c.id === +edit.dataset.edit!);
      if (closure) openClosureForm(closure, openClosureList);
      return;
    }
    const del = target.closest("[data-del]") as HTMLElement | null;
    if (del) {
      const closure = closures.find(c => c.id === +del.dataset.del!);
      if (!closure) return;
      // Erst schliessen, fragen, dann die Liste frisch aufbauen: sie muss den
      // geloeschten Eintrag ohnehin verlieren.
      closeModal();
      const ok = await confirmDialog({
        title: "Ferien löschen",
        message: `„${closure.name}" wird gelöscht. Bereits erzeugte Termine bleiben, wie sie sind.`,
        confirmLabel: "Löschen", danger: true,
      });
      if (ok) {
        try {
          await api.stundenplan.closures.delete(closure.id);
          await refresh();
          showToast("Ferien gelöscht");
        } catch (err: any) { showToast("Fehler: " + err.message); }
      }
      await openClosureList();
    }
  });
}


// ── Download ──

/**
 * Zeitraum für den Export. Vorbelegt ist die angezeigte Woche; wer den ganzen Jahrgang
 * braucht, zieht das Ende nach hinten. Die Termine werden frisch geladen, damit der
 * Export nicht an der Wochenansicht klebt.
 */
function openExportForm(fmt: "xlsx" | "pdf", mode: "klasse" | "dozent" | "alle") {
  if (mode === "alle") { openOverviewExportForm(fmt); return; }
  const forClass = mode === "klasse";
  if (forClass && !selectedClassId) { showToast("Bitte zuerst eine Klasse wählen"); return; }
  if (!forClass && lecturers.length === 0) { showToast("Es sind keine Dozenten angelegt"); return; }

  const className = classes.find(c => c.id === selectedClassId)?.name || "";
  const lecturerOpts = `<option value="">Alle Dozenten</option>` + lecturers
    .filter(l => l.active)
    .map(l => `<option value="${l.id}">${esc(`${l.firstName} ${l.lastName}`.trim())}</option>`).join("");

  openModal(
    `${forClass ? "Stundenplan" : "Dozentenplan"} als ${fmt === "pdf" ? "PDF" : "Excel"}`, `
    <div class="form-group">
      <label>${forClass ? "Klasse" : "Dozent"}</label>
      ${forClass
        ? `<input value="${esc(className)}" disabled />`
        : `<select id="x-lecturer">${lecturerOpts}</select>`}
    </div>
    <div class="form-grid">
      <div class="form-group"><label>Von *</label><input type="date" id="x-from" value="${esc(isoDate(weekStart))}" /></div>
      <div class="form-group"><label>Bis *</label><input type="date" id="x-to" value="${esc(isoDate(weekEnd()))}" /></div>
    </div>
    <div class="alert alert-info" style="font-size:.8125rem">
      ${fmt === "pdf"
        ? `Das PDF bildet je Woche das gewohnte Raster ab. Wochen ohne Unterricht werden übersprungen${forClass ? "" : ", jeder Dozent beginnt auf einer eigenen Seite"}.`
        : `Die Excel-Datei enthält eine Zeile je Unterrichtsstunde, sortiert nach ${forClass ? "Datum" : "Dozent und Datum"}.`}
    </div>
    <div class="alert alert-error hidden" id="x-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="x-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="x-go">Herunterladen</button>
    </div>`);

  const mb = modalBody();
  mb.querySelector("#x-cancel")!.addEventListener("click", closeModal);

  mb.querySelector("#x-go")!.addEventListener("click", async () => {
    const from = (mb.querySelector("#x-from") as HTMLInputElement).value;
    const to = (mb.querySelector("#x-to") as HTMLInputElement).value;
    const box = mb.querySelector("#x-error") as HTMLElement;
    if (!from || !to || to < from) {
      box.textContent = "Bitte einen gültigen Zeitraum wählen";
      box.classList.remove("hidden");
      return;
    }

    const btn = mb.querySelector("#x-go") as HTMLButtonElement;
    btn.disabled = true; btn.textContent = "Wird erstellt…";
    try {
      if (forClass) {
        const data = await api.stundenplan.plan(from, to, { klasse: selectedClassId! });
        await exportTimetable(fmt, { className, from, to, slots, lessons: data });
      } else {
        const pickedId = +(mb.querySelector("#x-lecturer") as HTMLSelectElement).value || null;
        // Ohne Auswahl alle Dozenten: einmal alles holen und gruppieren, statt je
        // Dozent eine eigene Abfrage zu stellen.
        const data = await api.stundenplan.plan(from, to, { dozent: pickedId ?? undefined });
        const wanted = pickedId
          ? lecturers.filter(l => l.id === pickedId)
          : lecturers.filter(l => l.active && data.some(x => x.lecturerId === l.id));
        if (wanted.length === 0) {
          box.textContent = "In diesem Zeitraum hat kein Dozent Unterricht.";
          box.classList.remove("hidden");
          btn.disabled = false; btn.textContent = "Herunterladen";
          return;
        }
        await exportLecturerTimetable(fmt, {
          from, to, slots,
          lecturers: wanted.map(l => ({
            name: `${l.firstName} ${l.lastName}`.trim(),
            lessons: data.filter(x => x.lecturerId === l.id),
          })),
        });
      }
      closeModal();
    } catch (err: any) {
      box.textContent = err.message;
      box.classList.remove("hidden");
      btn.disabled = false; btn.textContent = "Herunterladen";
    }
  });
}

/** Export der Ansicht „Alle Klassen" mit der dort gewählten Klassenauswahl. */
function openOverviewExportForm(fmt: "xlsx" | "pdf") {
  const cls = overviewClasses();
  if (cls.length === 0) { showToast("Bitte zuerst Klassen wählen"); return; }

  openModal(`Alle Klassen als ${fmt === "pdf" ? "PDF" : "Excel"}`, `
    <div class="form-group">
      <label>Klassen</label>
      <div style="font-size:.875rem;padding:.35rem 0">${cls.map(c => esc(c.name)).join(", ")}</div>
      <small class="text-muted">Die Auswahl kommt aus der Ansicht „Alle Klassen".</small>
    </div>
    <div class="form-grid">
      <div class="form-group"><label>Von *</label><input type="date" id="x-from" value="${esc(isoDate(weekStart))}" /></div>
      <div class="form-group"><label>Bis *</label><input type="date" id="x-to" value="${esc(isoDate(weekEnd()))}" /></div>
    </div>
    <div class="alert alert-info" style="font-size:.8125rem">
      ${fmt === "pdf"
        ? "Das PDF zeigt je Woche eine Seite mit allen Klassen nebeneinander. Wochen ohne Unterricht werden übersprungen."
        : "Die Excel-Datei zeigt je Woche Tag und Block als Zeilen und je Klasse die Spalten Fach, Dozent, Bemerkung und Raum."}
    </div>
    <div class="alert alert-error hidden" id="x-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="x-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="x-go">Herunterladen</button>
    </div>`);

  const mb = modalBody();
  mb.querySelector("#x-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#x-go")!.addEventListener("click", async () => {
    const from = (mb.querySelector("#x-from") as HTMLInputElement).value;
    const to = (mb.querySelector("#x-to") as HTMLInputElement).value;
    const box = mb.querySelector("#x-error") as HTMLElement;
    if (!from || !to || to < from) {
      box.textContent = "Bitte einen gültigen Zeitraum wählen";
      box.classList.remove("hidden");
      return;
    }
    const btn = mb.querySelector("#x-go") as HTMLButtonElement;
    btn.disabled = true; btn.textContent = "Wird erstellt…";
    try {
      const wanted = new Set(cls.map(c => +c.id));
      const data = (await api.stundenplan.plan(from, to)).filter(l => wanted.has(+l.classId));
      await exportOverview(fmt, {
        from, to, slots,
        classes: cls.map(c => ({ id: +c.id, name: c.name })),
        lessons: data,
      });
      closeModal();
    } catch (err: any) {
      box.textContent = err.message;
      box.classList.remove("hidden");
      btn.disabled = false; btn.textContent = "Herunterladen";
    }
  });
}

// ── Seite ──

/**
 * Schliesst die Auswahlklappen der Seite beim Klick daneben.
 *
 * Bewusst genau ein Listener fuer die Lebensdauer des Moduls: registriert man ihn je
 * render(), haengen nach ein paar Seitenwechseln mehrere davon an document und halten
 * die abgehaengten Knoten fest.
 */
let dropdownCloserBound = false;
function bindDropdownCloser() {
  if (dropdownCloserBound) return;
  dropdownCloserBound = true;
  document.addEventListener("click", (e) => {
    currentEl?.querySelectorAll<HTMLElement>(".dropdown-check.open").forEach(dd => {
      if (!dd.contains(e.target as Node)) dd.classList.remove("open");
    });
  });
}

/**
 * Neu laden und zeichnen, ohne die Scroll-Position zu verlieren.
 *
 * renderBody() ersetzt das ganze innerHTML, damit ist der gescrollte Container weg und
 * das Gitter springt zurueck an den Anfang. Beim Setzen einer Abwesenheit passiert das
 * nach jedem einzelnen Klick, was die Matrix unbenutzbar macht, sobald sie breiter als
 * der Bildschirm ist. Dieselbe Loesung wie renderGridPreservingScroll in planning.ts:
 * Position merken und im naechsten Frame zuruecksetzen, wenn der neue Container steht.
 */
async function refresh() {
  await loadWeek();
  const host = currentEl?.querySelector("#tp-body");
  const before = host?.querySelector(".planning-wrapper") as HTMLElement | null;
  const left = before?.scrollLeft ?? 0;
  const top = before?.scrollTop ?? 0;

  renderBody();

  if (left === 0 && top === 0) return;
  requestAnimationFrame(() => {
    const after = currentEl?.querySelector("#tp-body .planning-wrapper") as HTMLElement | null;
    if (after) { after.scrollLeft = left; after.scrollTop = top; }
  });
}

// ── Anwesenheit ──────────────────────────────────────────────────────

const ABSENCE_KIND_LABEL: Record<AbsenceKind, string> = {
  gemeldet: "Gemeldet, noch nicht eingestuft",
  entschuldigt: "Entschuldigt",
  unentschuldigt: "Unentschuldigt",
  beurlaubt: "Beurlaubt",
};

/**
 * Anwesenheitsmatrix: Schüler als Zeilen, Unterrichtsstunden als Spalten.
 *
 * Ausgefallene Stunden stehen bewusst als Spalte drin, aber ohne anklickbare Zelle: Sie
 * haben nicht stattgefunden, also kann dort niemand gefehlt haben. Genau dafür führt der
 * Plan den Ausfall als Zustand, statt den Termin zu löschen.
 */
function renderAttendance(): string {
  if (!attendance) return `<div class="alert alert-info">Bitte eine Klasse wählen.</div>`;
  if (attendance.students.length === 0) {
    return `<div class="alert alert-info">Dieser Klasse sind keine Schüler zugeordnet.</div>`;
  }
  if (attendance.lessons.length === 0) {
    return `<div class="alert alert-info">In dieser Woche liegt kein Unterricht für diese Klasse.</div>`;
  }

  const absent = new Set(attendance.cells.map(c => `${c.studentId}:${c.lessonId}`));
  const writable = canWrite();

  // Tagesgruppen für die Kopfzeile: sonst stünden zwanzig Spalten ohne Datumsbezug da.
  const byDay = new Map<string, Lesson[]>();
  for (const l of attendance.lessons) {
    if (!byDay.has(l.lessonDate)) byDay.set(l.lessonDate, []);
    byDay.get(l.lessonDate)!.push(l);
  }
  const days = [...byDay.entries()];

  const dayHeads = days.map(([date, ls]) =>
    `<th colspan="${ls.length}" class="att-daybreak">${esc(formatDate(date))}</th>`).join("");
  const lessonHeads = days.flatMap(([, ls]) => ls.map((l, i) =>
    `<th class="att-lesson${i === 0 ? " att-daybreak" : ""}"
         title="${esc(l.subjectName)} · ${esc(l.startTime)} bis ${esc(l.endTime)}${l.status === "entfall" ? " · entfällt" : ""}">
       ${esc(l.subjectShortName || l.subjectName)}
     </th>`)).join("");

  const rows = attendance.students.map(s => {
    const cells = days.flatMap(([, ls]) => ls.map((l, i) => {
      const daybreak = i === 0 ? " att-daybreak" : "";
      // Eine ausgefallene Stunde kann niemand versäumen, deshalb ist sie hier kein Feld,
      // sondern nur eine graue Lücke.
      if (l.status === "entfall") return `<td class="att-off${daybreak}"></td>`;

      const isAbsent = absent.has(`${s.id}:${l.id}`);
      return `<td class="att-cell${isAbsent ? " att-absent" : ""}${daybreak}"
                  data-student="${s.id}" data-lesson="${l.id}"
                  title="${esc(`${s.lastName}, ${s.firstName}`)} · ${esc(l.subjectName)}, ${esc(l.startTime)}">
                ${isAbsent ? "✕" : ""}
              </td>`;
    })).join("");
    // Name und Fehlstunden in einer Spalte, wie in der Praktikumsplanung: eine eigene
    // Spalte für eine Zahl wäre in einem Gitter aus zwanzig Kreuzen zu viel Platz.
    return `
      <tr>
        <td data-student-row="${s.id}">
          <div class="att-name">${esc(`${s.lastName}, ${s.firstName}`)}</div>
          <div class="student-sub">${s.absentHours > 0 ? `${esc(String(s.absentHours))} h gefehlt` : "vollständig da"}</div>
        </td>
        ${cells}
      </tr>`;
  }).join("");

  // Feste Spaltenbreiten über colgroup statt über min-width an der Zelle: Die Breite
  // richtete sich sonst nach dem längsten Fachkürzel, und die Spalten waren
  // unterschiedlich breit. Mit table-layout:fixed (siehe CSS) entscheidet allein das hier.
  const cols = `
    <colgroup>
      <col class="att-col-name" />
      ${attendance.lessons.map(() => `<col class="att-col-lesson" />`).join("")}
    </colgroup>`;

  return `
    <div class="planning-wrapper attendance-wrapper${writable ? " att-editable" : ""}">
      <table>
        ${cols}
        <thead>
          <tr>
            <th rowspan="2">Schüler</th>
            ${dayHeads}
          </tr>
          <tr>${lessonHeads}</tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <p class="text-muted" style="font-size:.8125rem;margin-top:.5rem">
      ${writable
        ? "Ein Klick auf eine Zelle kennzeichnet die Stunde als versäumt. Ein Klick auf den Namen öffnet längere Zeiträume und die Einstufung. Graue Spalten sind ausgefallene Stunden und zählen nicht."
        : "Du hast Leserechte. Änderungen sind mit der Rolle „Betrachter\" nicht möglich."}
    </p>`;
}

/**
 * Deckt diese Meldung genau diese eine Stunde ab, und sonst nichts?
 *
 * Bewusst streng: Eine ganztägige Meldung wird hier NICHT mitgezählt, auch nicht an einem
 * einzelnen Tag. Sonst nähme ein Klick auf eine Zelle stillschweigend den ganzen Tag weg,
 * und das ist mehr, als angeklickt wurde.
 */
function coversOnlyLesson(a: StudentAbsence, lesson: Lesson): boolean {
  return a.startDate === lesson.lessonDate && a.endDate === lesson.lessonDate
    && a.startTime === lesson.startTime && a.endTime === lesson.endTime;
}

/**
 * Zelle umschalten. Eintragen legt eine Meldung für genau diese Stunde an, als
 * „gemeldet": Wer im Unterricht abhakt, weiß meist noch nicht, ob es entschuldigt ist.
 * Eingestuft wird später über den Namen oder die Liste.
 */
async function toggleAttendance(studentId: number, lessonId: number) {
  if (!attendance) return;
  const lesson = attendance.lessons.find(l => l.id === lessonId);
  if (!lesson || lesson.status === "entfall") return;

  const isAbsent = attendance.cells.some(c => c.studentId === studentId && c.lessonId === lessonId);
  try {
    if (!isAbsent) {
      await api.stundenplan.attendance.create({
        studentId,
        startDate: lesson.lessonDate, endDate: lesson.lessonDate,
        startTime: lesson.startTime, endTime: lesson.endTime,
        kind: "gemeldet",
      });
    } else {
      // Nur eine Meldung, die genau diese Stunde abdeckt, lässt sich hier aufheben. Alles
      // Größere würde stillschweigend mehr wegnehmen, als angeklickt wurde.
      const covering = attendance.absences.filter(a =>
        a.studentId === studentId && a.status === "aktiv" &&
        a.startDate <= lesson.lessonDate && a.endDate >= lesson.lessonDate);
      const single = covering.find(a => coversOnlyLesson(a, lesson));
      if (!single) {
        showToast("Diese Abwesenheit gilt für mehr als diese Stunde. Bitte über den Namen bearbeiten.");
        return;
      }
      await api.stundenplan.attendance.retract(single.id);
    }
    await refresh();
  } catch (err: any) { showToast("Fehler: " + err.message); }
}

/**
 * Meldungen eines Schülers, anlegen, einstufen, zurücknehmen.
 *
 * Bewusst auf die angezeigte Woche begrenzt, so wie die Matrix dahinter: Hier arbeitet
 * jemand an einer Woche, nicht am Lebenslauf. Der vollständige Nachweis über die ganze
 * Ausbildung steht am Schüler selbst, unter „Schüler", Reiter „Fehlzeiten".
 *
 * Zurückgenommene Meldungen sind standardmäßig aus, sonst füllt sich die Liste mit
 * Einträgen, die nicht mehr gelten.
 */
let showAllAbsences = false;

async function openAbsenceDialog(studentId: number) {
  if (!attendance) return;
  const student = attendance.students.find(s => s.id === studentId);
  if (!student) return;
  const name = `${student.firstName} ${student.lastName}`.trim();
  const mine = attendance.absences
    .filter(a => a.studentId === studentId)
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  const visible = showAllAbsences ? mine : mine.filter(a => a.status === "aktiv");
  const hidden = mine.length - visible.length;

  const kindOptions = (selected: AbsenceKind) => (Object.keys(ABSENCE_KIND_LABEL) as AbsenceKind[])
    .map(k => `<option value="${k}" ${k === selected ? "selected" : ""}>${esc(ABSENCE_KIND_LABEL[k])}</option>`).join("");

  // Liste statt Tabelle: Fünf Spalten mit dem globalen Zellenpadding passen nicht in
  // einen 48rem breiten Dialog, das erzwang horizontales Scrollen und verschenkte
  // gleichzeitig Platz. Zeitraum und Herkunft stehen jetzt links untereinander, die
  // Bedienelemente rechts; auf schmalen Dialogen bricht die Zeile um.
  const rows = visible.map(a => `
    <div class="abs-row${a.status === "aktiv" ? "" : " abs-row--retracted"}">
      <div class="abs-main">
        <div class="abs-period">
          ${esc(formatDate(a.startDate))}${a.endDate !== a.startDate ? ` bis ${esc(formatDate(a.endDate))}` : ""}
        </div>
        <div class="abs-meta">
          ${a.startTime ? `${esc(a.startTime)} bis ${esc(a.endTime!)}` : "ganztags"}
          · ${a.source === "schueler" ? "Selbstmeldung" : "von der Schule"}
        </div>
      </div>
      ${a.status === "aktiv"
        ? `<div class="abs-actions">
             <select data-kind="${a.id}">${kindOptions(a.kind)}</select>
             <label class="abs-attest" title="Attest liegt vor">
               <input type="checkbox" data-attest="${a.id}" ${a.attestVorgelegt ? "checked" : ""} /> Attest
             </label>
             <button class="btn btn-sm btn-danger" data-retract="${a.id}">Zurücknehmen</button>
           </div>`
        : `<div class="abs-actions"><span class="badge badge-gray">Zurückgenommen</span></div>`}
    </div>`).join("");

  openModal(`Abwesenheit: ${name}`, `
    <fieldset>
      <legend>Neue Abwesenheit</legend>
      <div class="form-grid">
        <div class="form-group"><label>Von *</label><input type="date" id="ab-from" value="${esc(isoDate(weekStart))}" /></div>
        <div class="form-group"><label>Bis</label><input type="date" id="ab-to" /></div>
      </div>
      <div class="form-grid">
        <div class="form-group"><label>Von (Uhrzeit)</label><input type="time" id="ab-start" /></div>
        <div class="form-group"><label>Bis (Uhrzeit)</label><input type="time" id="ab-end" /></div>
      </div>
      <small class="text-muted">Beide Uhrzeiten leer lassen für einen ganzen Tag. „Bis" leer lassen für einen einzelnen Tag.</small>
      <div class="form-grid" style="margin-top:.75rem">
        <div class="form-group"><label>Art</label><select id="ab-kind">${kindOptions("entschuldigt")}</select></div>
      </div>
      <div class="form-group" style="margin-top:.75rem">
        <label class="checkbox-label"><input type="checkbox" id="ab-attest" /> Attest liegt vor</label>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary" id="ab-add">Abwesenheit eintragen</button>
      </div>
    </fieldset>

    <div style="display:flex;align-items:baseline;justify-content:space-between;gap:1rem;margin-top:1.5rem;flex-wrap:wrap">
      <div>
        <h3 style="margin:0">Gemeldet in dieser Woche</h3>
        <small class="text-muted">
          ${esc(`${formatDate(weekStart)} bis ${formatDate(weekEnd())}`)} ·
          Der vollständige Nachweis steht unter „Schüler", Reiter „Fehlzeiten".
        </small>
      </div>
      ${hidden > 0 || showAllAbsences
        ? `<button class="btn btn-sm btn-secondary" id="ab-toggle-all">${showAllAbsences ? "Zurückgenommene ausblenden" : `Zurückgenommene zeigen (${hidden})`}</button>`
        : ""}
    </div>
    ${visible.length === 0
      ? `<div class="empty-state">${mine.length === 0
          ? "In dieser Woche ist für diesen Schüler nichts gemeldet."
          : "Alle Meldungen dieser Woche wurden zurückgenommen. Oben einblenden."}</div>`
      : `<div class="abs-list">${rows}</div>`}
    <div class="alert alert-error hidden" id="ab-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="ab-close">Schließen</button>
    </div>`, { wide: true, stickyActions: true });

  const mb = modalBody();
  const showError = (message: string) => {
    const box = mb.querySelector("#ab-error") as HTMLElement;
    box.textContent = message;
    box.classList.remove("hidden");
  };
  mb.querySelector("#ab-close")!.addEventListener("click", closeModal);

  // Neu zeichnen statt Zeilen ein- und auszublenden: Die Zahl im Knopf und der leere
  // Zustand haengen mit dran, und der Dialog liest ohnehin nur aus dem geladenen Stand.
  mb.querySelector("#ab-toggle-all")?.addEventListener("click", () => {
    showAllAbsences = !showAllAbsences;
    openAbsenceDialog(studentId);
  });

  mb.querySelector("#ab-add")!.addEventListener("click", async () => {
    const startDate = (mb.querySelector("#ab-from") as HTMLInputElement).value;
    if (!startDate) { showError("Von ist ein Pflichtfeld"); return; }
    const startTime = (mb.querySelector("#ab-start") as HTMLInputElement).value || null;
    const endTime = (mb.querySelector("#ab-end") as HTMLInputElement).value || null;
    if (!!startTime !== !!endTime) {
      showError("Bitte beide Uhrzeiten angeben, oder beide leer lassen");
      return;
    }
    try {
      await api.stundenplan.attendance.create({
        studentId,
        startDate,
        endDate: (mb.querySelector("#ab-to") as HTMLInputElement).value || startDate,
        startTime, endTime,
        kind: (mb.querySelector("#ab-kind") as HTMLSelectElement).value,
        attestVorgelegt: (mb.querySelector("#ab-attest") as HTMLInputElement).checked,
      });
      await refresh();
      closeModal();
      showToast("Abwesenheit eingetragen");
    } catch (err: any) { showError(err.message); }
  });

  mb.querySelectorAll<HTMLSelectElement>("[data-kind]").forEach(sel => {
    sel.addEventListener("change", async () => {
      try {
        await api.stundenplan.attendance.classify(+sel.dataset.kind!, { kind: sel.value as AbsenceKind });
        await refresh();
        showToast("Einstufung gespeichert");
      } catch (err: any) { showError(err.message); }
    });
  });

  mb.querySelectorAll<HTMLInputElement>("[data-attest]").forEach(box => {
    box.addEventListener("change", async () => {
      try {
        await api.stundenplan.attendance.classify(+box.dataset.attest!, { attestVorgelegt: box.checked });
        await refresh();
      } catch (err: any) { showError(err.message); }
    });
  });

  mb.querySelectorAll<HTMLButtonElement>("[data-retract]").forEach(btn => {
    btn.addEventListener("click", async () => {
      try {
        await api.stundenplan.attendance.retract(+btn.dataset.retract!);
        await refresh();
        closeModal();
        showToast("Meldung zurückgenommen");
      } catch (err: any) { showError(err.message); }
    });
  });
}

/**
 * Was in der angezeigten Woche vom Plan abweicht, als Tagesliste. Der Blick, den man
 * morgens braucht: was fällt heute aus, und wer springt ein.
 *
 * Bewusst über alle Klassen, auch in der Klassenansicht. Wer den Aushang macht, braucht
 * das ganze Haus, und die Klasse steht ohnehin an jeder Zeile.
 */
async function openChangeList() {
  openModal("Vertretungen der Woche", `<div class="spinner"></div>`, { wide: true });

  let changes: Lesson[];
  try {
    changes = await api.stundenplan.changes(isoDate(weekStart), isoDate(weekEnd()));
  } catch (err: any) {
    modalBody().innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    return;
  }

  const describe = (l: Lesson): string => {
    if (l.status === "entfall") return "Fällt aus";
    const parts: string[] = [];
    if (l.substituteLecturerName) {
      parts.push(l.lecturerName
        ? `${esc(l.substituteLecturerName)} statt ${esc(l.lecturerName)}`
        : esc(l.substituteLecturerName));
    }
    if (l.substituteRoom) {
      parts.push(l.room ? `${esc(l.substituteRoom)} statt ${esc(l.room)}` : esc(l.substituteRoom));
    }
    return parts.join(", ") || "Vertretung";
  };

  const rows = changes.map(l => `
    <tr${l.status === "entfall" ? ` style="opacity:.7"` : ""}>
      <td style="white-space:nowrap">${esc(formatDate(l.lessonDate))}</td>
      <td style="white-space:nowrap">${esc(l.startTime)} bis ${esc(l.endTime)}</td>
      <td>${esc(l.className)}</td>
      <td>${esc(l.subjectShortName || l.subjectName)}</td>
      <td>
        <span class="${l.status === "entfall" ? "ab-cancelled" : "ab-substitute"}">${l.status === "entfall" ? "Entfällt" : "Vertretung"}</span>
        <span style="margin-left:.5rem">${describe(l)}</span>
      </td>
      <td class="text-muted">${esc(l.changeNote || "")}</td>
    </tr>`).join("");

  modalBody().innerHTML = `
    <div style="font-size:.875rem;color:var(--gray-600);margin-bottom:.75rem">
      ${esc(`${formatDate(weekStart)} bis ${formatDate(weekEnd())}`)}
    </div>
    ${changes.length === 0
      ? `<div class="empty-state">In dieser Woche weicht nichts vom Plan ab.</div>`
      : `<div class="table-wrap"><table>
          <thead><tr>
            <th>Datum</th><th>Zeit</th><th>Klasse</th><th>Fach</th><th>Änderung</th><th>Bemerkung</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table></div>`}
    <div class="form-actions">
      <button class="btn btn-secondary" id="ch-close">Schließen</button>
    </div>`;

  modalBody().querySelector("#ch-close")!.addEventListener("click", closeModal);
}

function renderBody() {
  const host = currentEl?.querySelector("#tp-body");
  if (!host) return;

  const { week, year } = isoWeek(weekStart);
  const label = `KW ${week} · ${formatDate(weekStart)} bis ${formatDate(weekEnd())} · ${year}`;
  const activeRooms = rooms.filter(r => r.active);
  // Die Raumansicht steht nur zur Wahl, wenn es überhaupt Räume gibt. Ein Umschalter,
  // hinter dem nichts liegt, wäre nur eine Sackgasse.
  // Klasse und Raum sind zwei Sichten auf denselben Plan, deshalb eine Auswahl. Die
  // Anwesenheit ist dagegen eine andere Arbeit und liegt als eigene Schaltfläche in der
  // Kopfzeile; in einer Auswahl zwischen zwei Plan-Ansichten war sie nicht zu finden.
  // Die Raumansicht erscheint nur, wenn es Räume gibt: ein Umschalter, hinter dem nichts
  // liegt, wäre eine Sackgasse.
  // „Alle Klassen" steht immer zur Wahl, „Nach Raum" nur mit Räumen.
  const modeSelect = viewMode === "anwesenheit" ? "" : `
    <select id="tp-mode" title="Ansicht">
      <option value="klasse"${viewMode === "klasse" ? " selected" : ""}>Nach Klasse</option>
      <option value="alle"${viewMode === "alle" ? " selected" : ""}>Alle Klassen</option>
      ${activeRooms.length ? `<option value="raum"${viewMode === "raum" ? " selected" : ""}>Nach Raum</option>` : ""}
    </select>`;
  const selector = viewMode === "raum"
    ? `<select id="tp-room">${activeRooms.map(r =>
        `<option value="${r.id}" ${selectedRoomId === r.id ? "selected" : ""}>${esc(r.name)}</option>`).join("")}</select>`
    : viewMode === "alle"
      ? `<div class="dropdown-check" id="tp-classes">
           <button type="button" class="btn btn-secondary dropdown-check-btn">Klassen wählen</button>
           <div class="dropdown-check-panel">
             ${classes.filter(c => c.active !== false).map(c =>
               `<label class="dropdown-check-item"><input type="checkbox" value="${c.id}" ${overviewClassIds.includes(+c.id) ? "checked" : ""} /> ${esc(c.name)}</label>`).join("")}
           </div>
         </div>`
      : `<select id="tp-class">${classes.map(c =>
          `<option value="${c.id}" ${selectedClassId === c.id ? "selected" : ""}>${esc(c.name)}</option>`).join("")}</select>`;

  const empty = viewMode === "raum"
    ? (activeRooms.length === 0 ? "Es sind keine Räume angelegt." : "")
    : (classes.length === 0 ? "Es sind keine Klassen angelegt." : "");

  host.innerHTML = `
    <div class="filter-bar" style="align-items:center">
      ${modeSelect}
      ${selector}
      <div style="display:flex;gap:.375rem;align-items:center;margin-left:auto">
        <button class="btn btn-secondary btn-sm" id="tp-prev">‹</button>
        <button class="btn btn-secondary btn-sm" id="tp-today">Heute</button>
        <button class="btn btn-secondary btn-sm" id="tp-next">›</button>
      </div>
    </div>
    <div style="font-size:.875rem;color:var(--gray-600);margin-bottom:.75rem">${esc(label)}</div>
    ${empty
      ? `<div class="alert alert-info">${esc(empty)}</div>`
      : viewMode === "anwesenheit"
        ? renderAttendance()
        : viewMode === "alle"
          ? renderOverview()
          : renderGrid()}`;

  host.querySelector("#tp-prev")!.addEventListener("click", () => { weekStart = addDays(weekStart, -7); refresh(); });
  host.querySelector("#tp-next")!.addEventListener("click", () => { weekStart = addDays(weekStart, 7); refresh(); });
  host.querySelector("#tp-today")!.addEventListener("click", () => { weekStart = mondayOf(new Date()); refresh(); });

  host.querySelector("#tp-mode")?.addEventListener("change", (e) => {
    viewMode = (e.target as HTMLSelectElement).value as ViewMode;
    if (viewMode === "raum" && selectedRoomId === null) {
      selectedRoomId = rooms.find(r => r.active)?.id ?? null;
    }
    refresh();
  });
  const classPicker = host.querySelector<HTMLElement>("#tp-classes");
  if (classPicker) {
    const handle = initDropdownCheck(classPicker, values => {
      overviewClassIds = values.map(Number);
      storeOverviewSelection();
      refresh();
    });
    handle.setValues(overviewClassIds.map(String));
  }
  host.querySelector("#tp-class")?.addEventListener("change", (e) => {
    selectedClassId = +(e.target as HTMLSelectElement).value;
    refresh();
  });
  host.querySelector("#tp-room")?.addEventListener("change", (e) => {
    selectedRoomId = +(e.target as HTMLSelectElement).value;
    refresh();
  });
  if (viewMode === "anwesenheit") {
    host.querySelector(".attendance-wrapper table")?.addEventListener("click", (e) => {
      if (!canWrite()) return;
      const target = e.target as HTMLElement;

      const row = target.closest("[data-student-row]") as HTMLElement | null;
      // Beim Öffnen wieder auf die aktuellen Meldungen zurück: Wer beim vorigen Schüler
      // die zurückgenommenen eingeblendet hat, meint damit nicht den nächsten.
      if (row) { showAllAbsences = false; openAbsenceDialog(+row.dataset.studentRow!); return; }

      const cell = target.closest("[data-student][data-lesson]") as HTMLElement | null;
      if (cell) toggleAttendance(+cell.dataset.student!, +cell.dataset.lesson!);
    });
    return;
  }

  // Übersicht: Klick auf einen Termin öffnet ihn, eine freie Zelle legt Unterricht für
  // genau diese Klasse an.
  host.querySelector(".overview-wrapper table")?.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const line = target.closest("[data-lesson]") as HTMLElement | null;
    if (line) {
      const lesson = lessons.find(l => l.id === +line.dataset.lesson!);
      if (lesson) openLessonForm({ lesson });
      return;
    }
    if (!canWrite()) return;
    const cell = target.closest("td[data-class]") as HTMLElement | null;
    if (cell) {
      openLessonForm({
        day: cell.dataset.day,
        slot: slots.find(s => s.id === +(cell.dataset.slot || "")),
        classId: +cell.dataset.class!,
      });
    }
  });

  host.querySelector(".timetable-wrapper table")?.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;

    const card = target.closest("[data-lesson]") as HTMLElement | null;
    if (card) {
      const lesson = lessons.find(l => l.id === +card.dataset.lesson!);
      if (lesson) openLessonForm({ lesson });
      return;
    }
    // Aus einer leeren Zelle heraus anzulegen setzt eine Klasse voraus. In der
    // Raumansicht ist sie nicht bestimmt, deshalb bleibt sie dort zum Nachsehen.
    if (!canWrite() || viewMode === "raum") return;

    const cell = target.closest("td[data-day]") as HTMLElement | null;
    if (cell) {
      openLessonForm({
        day: cell.dataset.day,
        slot: slots.find(s => s.id === +(cell.dataset.slot || "")),
      });
    }
  });
}

export async function render(el: HTMLElement) {
  currentEl = el;
  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;

  try {
    await loadBase();
    await loadWeek();
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    return;
  }

  el.innerHTML = `
    <div class="page-header">
      <h1>Stundenplan</h1>
      <div style="display:flex;gap:.5rem">
        <button class="btn btn-secondary" id="tp-attendance">Anwesenheit</button>
        ${writeOnly(`
        <div class="dropdown-check" id="dd-manage">
          <button class="btn btn-secondary dropdown-check-btn">Verwalten</button>
          <div class="dropdown-check-panel">
            <div class="dropdown-check-item" id="tp-changes">Vertretungen der Woche</div>
            <div class="dropdown-divider"></div>
            <div class="dropdown-check-item" id="tp-add-closure">Ferien eintragen</div>
            <div class="dropdown-check-item" id="tp-manage-closures">Ferien verwalten</div>
            ${vorplanungEnabled() ? `
              <div class="dropdown-divider"></div>
              <div class="dropdown-check-item" id="tp-visible">Woche sichtbar machen</div>
              <div class="dropdown-check-item" id="tp-confirm">Woche als bestätigt markieren</div>` : ""}
          </div>
        </div>`)}
        <div class="dropdown-check" id="dd-download">
          <button class="btn btn-secondary dropdown-check-btn">Download</button>
          <div class="dropdown-check-panel">
            <div class="dropdown-check-item" id="tp-xlsx">Stundenplan als Excel</div>
            <div class="dropdown-check-item" id="tp-pdf">Stundenplan als PDF</div>
            <div class="dropdown-divider"></div>
            <div class="dropdown-check-item" id="tp-alle-xlsx">Alle Klassen als Excel</div>
            <div class="dropdown-check-item" id="tp-alle-pdf">Alle Klassen als PDF</div>
            <div class="dropdown-divider"></div>
            <div class="dropdown-check-item" id="tp-doz-xlsx">Dozentenplan als Excel</div>
            <div class="dropdown-check-item" id="tp-doz-pdf">Dozentenplan als PDF</div>
          </div>
        </div>
      </div>
    </div>
    ${readOnlyNotice()}
    <div id="tp-body"></div>`;

  bindDropdownCloser();

  // Umschalter zwischen Plan und Anwesenheit. Die Beschriftung sagt, wohin es geht, statt
  // sich auf einen aktiven Zustand zu verlassen, den man an einer Schaltfläche leicht
  // übersieht.
  const attendanceBtn = el.querySelector<HTMLButtonElement>("#tp-attendance")!;
  const paintAttendanceBtn = () => {
    attendanceBtn.textContent = viewMode === "anwesenheit" ? "Zum Stundenplan" : "Anwesenheit";
    attendanceBtn.classList.toggle("btn-primary", viewMode === "anwesenheit");
    attendanceBtn.classList.toggle("btn-secondary", viewMode !== "anwesenheit");
  };
  paintAttendanceBtn();
  attendanceBtn.addEventListener("click", async () => {
    viewMode = viewMode === "anwesenheit" ? "klasse" : "anwesenheit";
    paintAttendanceBtn();
    await refresh();
  });

  const ddDownload = el.querySelector("#dd-download")!;
  ddDownload.querySelector(".dropdown-check-btn")!.addEventListener("click", () => ddDownload.classList.toggle("open"));
  el.querySelector("#tp-xlsx")!.addEventListener("click", () => {
    ddDownload.classList.remove("open"); openExportForm("xlsx", "klasse");
  });
  el.querySelector("#tp-pdf")!.addEventListener("click", () => {
    ddDownload.classList.remove("open"); openExportForm("pdf", "klasse");
  });
  el.querySelector("#tp-alle-xlsx")!.addEventListener("click", () => {
    ddDownload.classList.remove("open"); openExportForm("xlsx", "alle");
  });
  el.querySelector("#tp-alle-pdf")!.addEventListener("click", () => {
    ddDownload.classList.remove("open"); openExportForm("pdf", "alle");
  });
  el.querySelector("#tp-doz-xlsx")!.addEventListener("click", () => {
    ddDownload.classList.remove("open"); openExportForm("xlsx", "dozent");
  });
  el.querySelector("#tp-doz-pdf")!.addEventListener("click", () => {
    ddDownload.classList.remove("open"); openExportForm("pdf", "dozent");
  });

  const ddManage = el.querySelector("#dd-manage");
  if (ddManage) {
    ddManage.querySelector(".dropdown-check-btn")!.addEventListener("click", () => ddManage.classList.toggle("open"));
    const close = () => ddManage.classList.remove("open");
    el.querySelector("#tp-changes")!.addEventListener("click", () => { close(); openChangeList(); });
    el.querySelector("#tp-add-closure")!.addEventListener("click", () => { close(); openClosureForm(); });
    el.querySelector("#tp-manage-closures")!.addEventListener("click", () => { close(); openClosureList(); });
    el.querySelector("#tp-visible")?.addEventListener("click", close);
    el.querySelector("#tp-confirm")?.addEventListener("click", close);
  }

  const bulk = async (fn: (d: any) => Promise<{ updated: number }>, done: string) => {
    // Beide Sammelaktionen gelten fuer die angezeigte Woche EINER Klasse. In der
    // Raumansicht ist keine bestimmt, und ohne classId traefe die Aktion die ganze Schule.
    if (viewMode === "raum") {
      showToast("Bitte zuerst auf die Klassenansicht wechseln");
      return;
    }
    // In der Übersicht gilt die Aktion für jede gewählte Klasse einzeln, nie für die
    // ganze Schule.
    const classIds = viewMode === "alle" ? overviewClasses().map(c => +c.id) : [selectedClassId];
    if (classIds.length === 0 || classIds.some(id => !id)) { showToast("Bitte zuerst eine Klasse wählen"); return; }
    try {
      let updated = 0;
      for (const classId of classIds) {
        updated += (await fn({ classId, von: isoDate(weekStart), bis: isoDate(weekEnd()) })).updated;
      }
      const result = { updated };
      await refresh();
      if (result.updated === 0) { showToast("Nichts zu ändern"); return; }
      // Die Zahl der eingereihten Mails (notified) zeigen wir bewusst nicht: ohne Bezugsgröße
      // und ohne Namen kann ein Planer nichts damit anfangen, und zugestellt ist damit noch nichts.
      showToast(`${result.updated} ${done}`);
    } catch (err: any) { showToast("Fehler: " + err.message); }
  };
  el.querySelector("#tp-visible")?.addEventListener("click", () =>
    bulk(api.stundenplan.lessons.makeAllVisible, "Termine sichtbar gemacht"));
  el.querySelector("#tp-confirm")?.addEventListener("click", () =>
    bulk(api.stundenplan.lessons.confirmAll, "Termine bestätigt"));

  renderBody();
}
